export interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    type: 'added' | 'changed' | 'deprecated' | 'removed' | 'fixed' | 'security';
    items: string[];
  }[];
}

export const CHANGELOG_DATA: ChangelogEntry[] = [
  {
    version: '1.26.2',
    date: '2026-10-22',
    sections: [
      {
        type: 'fixed',
        items: [
          'ARC: los códigos 9xx de Hikvision (SIM, Wi-Fi, radio inalámbrica, red, módulo o teclado desconectado) ahora se muestran como fallas y no como armado/desarmado. Se corrigió también el historial: 23.388 eventos, con registro de la corrección.',
          'ARC: los botones de emergencia del panel (810/811/847/989) ahora se registran como alarma crítica.',
          'Los clientes ya no reciben avisos falsos de "desarmado" por fallas de comunicación del panel.',
          'El personal de ASI ya no recibe un push por cada armado/desarmado de los clientes hecho desde la app.',
          'El video de las cámaras ya no arranca con ~1 s de pantalla gris, y abre más rápido.',
        ],
      },
      {
        type: 'security',
        items: [
          'Las notificaciones de alarma solo se generan a partir de eventos reales, y nunca se repiten para el mismo evento.',
          'Un solicitante ya no puede elegirse un rol mayor al pedir unirse a una cuenta, y una cuenta nunca queda sin propietario activo.',
          'Armar o desarmar respeta los permisos por sitio de cada miembro.',
          'Las rutas de la app móvil en el gateway (hablar por la cámara, liberar streams) exigen sesión.',
        ],
      },
    ],
  },
  {
    version: '1.26.1',
    date: '2026-10-12',
    sections: [
      {
        type: 'added',
        items: [
          'La ficha de cada sitio (`/cuentas` → sitio) ahora muestra de un vistazo si las cámaras están configuradas en modo "video en vivo" o "snapshot cada 5s", sin tener que entrar a Editar.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Al mover una cámara ya registrada a otro sitio desde el escáner de red, el sistema ya no deja un registro "fantasma" del sitio anterior — se actualiza el registro existente en vez de crear uno nuevo.',
        ],
      },
    ],
  },
  {
    version: '1.26.0',
    date: '2026-10-08',
    sections: [
      {
        type: 'added',
        items: [
          'Nuevo flujo de alta de paneles: ahora se puede generar el código QR de un panel/sitio desde el momento en que se sincroniza desde HPP, sin necesidad de crear primero una cuenta para él. El cliente que lo escanea queda como dueño de una cuenta nueva, o el panel se vincula directo a su cuenta existente si ya tenía una de solo app — automáticamente, según corresponda.',
          'El código QR del panel ahora vive en el sitio, no en la cuenta: una cuenta con varios sitios/paneles puede generar un código independiente para cada uno, sin mezclar paneles entre sí.',
          'Nueva acción "Liberar sitio" (`/cuentas` → ficha de cuenta → Sitios): al dar de baja un panel, el operador puede soltarlo de la cuenta para que quede disponible para un cliente distinto, y se ofrece revertir la cuenta a modo "solo app" si se quedó sin sitios.',
          'En `/devices/panels`, pestaña "Sin cuenta": botón para generar el QR de onboarding directo desde el detalle de cada panel sin cuenta.',
          'Pantalla "Solicitar info" (mobile-client, clientes de solo app): ahora ofrece dos opciones claras — agregar alarma/cámaras, o solicitar otros servicios (guardias, escoltas, cursos, etc.) — en vez de una opción redundante con el escaneo de QR.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'El código QR impreso en el sticker del panel ahora es reutilizable: antes, una vez canjeado quedaba bloqueado para siempre y había que generar uno nuevo (imposible de reimprimir en la práctica). Ahora el mismo código se reactiva solo cuando el sitio se libera.',
          '`/devices/panels` ya refleja en tiempo real cuándo un panel se vincula o se libera de una cuenta, sin tener que resincronizar HPP manualmente.',
          'Al escanear el QR de un panel con una cuenta de solo app ya existente, la app ahora navega al Inicio al terminar en vez de quedarse en la misma pantalla, y el botón dice "Vincular panel" en vez de "Registrarme" cuando corresponde.',
          'Los subtítulos del menú de Configuración (mobile-client) ya no se cortan a la mitad — ahora se pueden leer completos en dos líneas.',
          'Menos espacio desperdiciado arriba del logo en Inicio, Alarmas, Dispositivos y Perfil (mobile-client).',
          'El slide-to-SOS ya no se queda trabado visualmente sobre el botón si cancelas el aviso de cobro de patrulla adicional.',
          'La tarjeta "Amplía tu servicio" (Perfil, clientes de solo app) tenía muy poco margen interno y los botones se veían apilados — ahora van lado a lado con más espacio.',
        ],
      },
    ],
  },
  {
    version: '1.25.0',
    date: '2026-10-06',
    sections: [
      {
        type: 'added',
        items: [
          'Nuevo sistema de control de capacidad de miembros por cuenta: las cuentas gestionadas por ASI ahora tienen un cupo de miembros con acceso a sitios (3 por sitio, ampliable en bloques de 3) y un cupo separado de licencias de alertas por app, según el plan de suscripción.',
          'La pantalla de Miembros (app y `/cuentas`) ahora muestra cuántos cupos de cada tipo se han usado y cuántos quedan disponibles.',
          'Desde `/cuentas`, un administrador puede comprar una ampliación de cupo (+3 miembros) para un sitio específico directamente desde la ficha de la cuenta.',
          'Cada sitio (`/cuentas` → ficha de sitio) ahora muestra cuántos miembros tiene afiliados.',
          'Nuevo control por miembro para asignar o quitar su licencia de alertas por app, sin afectar su acceso a sitios/cámaras.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Las invitaciones a nuevos miembros en cuentas gestionadas ya no se bloqueaban incorrectamente por un conteo que no reflejaba el cupo real de licencias disponibles.',
        ],
      },
    ],
  },
  {
    version: '1.24.22',
    date: '2026-10-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'El despacho de patrulla ya respeta el checkbox "Despacho de patrulla" del editor de planes (`/admin/plans`) — antes un campo interno distinto podía seguir bloqueando SOS/Pánico aunque el checkbox estuviera activado.',
          'Corregido un caso donde un sensor repitiendo la misma alarma en una zona generaba múltiples tickets y despachos de patrulla duplicados en pocos minutos — ahora se reutiliza el ticket ya abierto para esa zona mientras siga sin cerrar.',
          'El filtro "Operador" en Auditoría (`/audit`) mostraba a todos los clientes del sistema, no solo a quienes pueden operar (operador/admin/super admin).',
          'En la pantalla de Perfil (mobile-client), el nombre del plan personal activo se muestra ahora de forma más clara y legible.',
          'Al salir de una alerta en curso con el botón/gesto de atrás del teléfono (mobile-client), ya no te regresa a Inicio — te lleva correctamente al Historial.',
          'El registro con una contraseña débil o filtrada ahora explica el motivo específico y sugiere una contraseña segura, en vez de un error genérico.',
          'Corregido un caso donde la suscripción personal activada desde el Paywall podía no reflejarse correctamente en cuentas con doble membresía (gestionada + personal).',
          'Corregido el cálculo de cobro por despacho de patrulla en planes PRO/PREMIUM sin cuota mensual gratuita configurada.',
        ],
      },
      {
        type: 'added',
        items: [
          'Ahora puedes tocar tu foto de perfil para verla en pantalla completa, igual que en WhatsApp.',
        ],
      },
      {
        type: 'changed',
        items: [
          'El punto de notificación pendiente en Perfil y Dispositivos (mobile-client) ya solo aparece cuando realmente hay notificaciones sin leer.',
          'El ícono de campana en Dispositivos (mobile-client) ahora abre Notificaciones en vez de Historial.',
        ],
      },
    ],
  },
  {
    version: '1.24.21',
    date: '2026-10-05',
    sections: [
      {
        type: 'security',
        items: [
          'Protección contra fuerza bruta en el login (web y ambas apps móviles): 5 intentos fallidos bloquean la cuenta por 15 minutos.',
          'Las cuentas nuevas creadas por un operador/admin ya no reciben una contraseña genérica predecible — ahora se genera una contraseña aleatoria por usuario.',
          'Supabase Auth ahora rechaza contraseñas filtradas conocidas (HaveIBeenPwned) y exige un mínimo de 8 caracteres.',
        ],
      },
      {
        type: 'added',
        items: [
          'Protección contra alertas duplicadas: ya no se puede enviar una nueva alerta (SOS, Pánico, Incendio, Médica, Llamada) mientras haya otra del mismo tipo en curso. Hay que esperar a que se atienda o cancele la anterior.',
          'Las cuentas gestionadas por ASI ahora también pueden activar una suscripción personal (SOS desde la app, dondequiera que estés) — antes el catálogo de planes solo era visible para cuentas auto-registradas.',
          'Desde `/admin/plans`, un plan nuevo ya no está obligado a clasificarse como TRIAL/BASIC/PRO/PREMIUM — se puede crear con un nombre y código totalmente personalizados (p. ej. "Familiar").',
        ],
      },
      {
        type: 'changed',
        items: [
          'Enviar cualquier alerta desde la app (incluyendo Incendio, Médica y Llamada, antes siempre permitidas) ahora requiere una suscripción activa, igual que SOS y Pánico.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'El banner "alerta activa" en Inicio (mobile-client) mostraba el tipo en el código interno (CALL/FIRE/MEDICAL) en vez de en español (Llamada/Incendio/Médica).',
        ],
      },
    ],
  },
  {
    version: '1.24.20',
    date: '2026-09-21',
    sections: [
      {
        type: 'added',
        items: [
          'Antes de armar, si un panel reporta una falla técnica activa (batería faltante, corte de energía, etc.) la app ahora te avisa y te deja elegir entre cancelar o armar de todas formas, en vez de esperar en silencio a que el armado falle.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Corregido un caso donde la app podía cerrar la sesión del usuario justo después de iniciar sesión correctamente (mobile-client).',
        ],
      },
    ],
  },
  {
    version: '1.24.19',
    date: '2026-09-18',
    sections: [
      {
        type: 'added',
        items: [
          'Al armar un área con retardo de salida, la app ahora muestra la cuenta regresiva real en segundos (leída en vivo desde el propio panel) en vez de un texto genérico "Activando...".',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Se corrigió un caso donde el aviso de "activando" de un área desaparecía antes de tiempo al armar otra área del mismo panel, o al llegar la notificación de confirmación.',
        ],
      },
    ],
  },
  {
    version: '1.24.18',
    date: '2026-09-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'El push de armado/desarmado dej\u00f3 de llegarte tambi\u00e9n a ti mismo por error. Ahora vuelve a llegarte esa confirmaci\u00f3n inmediata siempre; solo se omite el aviso duplicado y tard\u00edo de tu propia acci\u00f3n.',
        ],
      },
    ],
  },
  {
    version: '1.24.17',
    date: '2026-09-17',
    sections: [
      {
        type: 'changed',
        items: [
          'La pantalla de Dispositivos carga instant\u00e1neamente tras reabrir la app despu\u00e9s de un rato suspendida, mostrando los \u00faltimos datos conocidos mientras se actualizan en segundo plano.',
          'Las notificaciones de recordatorio de armado y zona insegura ya no muestran un aviso adicional dentro de la app, solo la notificaci\u00f3n del sistema.',
          'Al armar o desarmar desde la app, ya no te llega una notificaci\u00f3n push de tu propia acci\u00f3n (ya la ves reflejada al instante en la app). El resto de miembros de la cuenta s\u00ed la reciben.',
        ],
      },
    ],
  },
  {
    version: '1.24.16',
    date: '2026-09-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Crash de video definitivo en cliente y patrol: las dos librer\u00edas de video de la app necesitaban versiones incompatibles de un componente nativo compartido. Se corrigi\u00f3 una de ellas para que ambas usen la misma versi\u00f3n sin romperse.',
          'Teclado tapando los campos en la pantalla de inicio de sesi\u00f3n y otros formularios (cliente).',
          'Video adjunto en el chat de una alerta se ve\u00eda como un cuadro negro en vez de reproducirse (cliente y patrol).',
        ],
      },
      {
        type: 'changed',
        items: [
          'Al reproducir un video en el chat de una alerta, cualquier otro video que estuviera sonando se pausa autom\u00e1ticamente.',
        ],
      },
    ],
  },
  {
    version: '1.24.15',
    date: '2026-09-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'El crash al reproducir video en alertas SOS persistía tras el fix anterior: la causa real era un conflicto de versión de una librería nativa de video (androidx.media3) entre dos componentes de la app, no la versión de expo-video. Se forzó una única versión consistente para toda la app.',
        ],
      },
    ],
  },
  {
    version: '1.24.14',
    date: '2026-09-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Crash al enviar o ver video en alertas SOS (confirmado con logs en dispositivo real): la app cliente ten\u00eda fijada una versi\u00f3n de expo-video incompatible con react-native-video, causando que el reproductor de video del chat de la alerta crasheara siempre. Corregido fijando la versi\u00f3n correcta.',
        ],
      },
    ],
  },
  {
    version: '1.24.13',
    date: '2026-09-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Cuentas creadas por auto-registro ("Crear mi propia cuenta" en la app móvil) ya no podían enviar patrullas ilimitadas cobrando factura cada vez: ahora, igual que un plan Básico, no incluyen despacho de patrulla (solo notificación al centro de monitoreo). El botón SOS sigue funcionando siempre; lo que se bloquea es el despacho físico de patrulla.',
          'Cierre de sesión en otro dispositivo: antes solo se detectaba al volver a abrir la app (foreground). Ahora la app cliente escucha el cambio de sesión en tiempo real y cierra la sesión de inmediato en el dispositivo antiguo.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Pantalla de bienvenida (onboarding) de la app cliente: la instrucción "Elige cómo quieres usar la aplicación" ahora es un banner más visible en vez de un texto pequeño y tenue.',
        ],
      },
    ],
  },
  {
    version: '1.24.12',
    date: '2026-09-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'La tarjeta de inicio de sesión se veía amarilla en modo oscuro (usaba el color "primary", que en modo oscuro es dorado). Ahora usa el navy fijo del sidebar, que no cambia entre modo claro/oscuro.',
        ],
      },
    ],
  },
  {
    version: '1.24.11',
    date: '2026-09-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Un operador ahora puede editar los datos de un sitio/panel (botón "Editar" y la clave de verificación en el detalle del sitio), y crear/editar/eliminar zonas de zonificación. Tenía el permiso "accounts.manage", pero esa pantalla en realidad depende de "alarms.manage" (un permiso distinto) — ya se le asignó al rol operador.',
        ],
      },
    ],
  },
  {
    version: '1.24.10',
    date: '2026-09-14',
    sections: [
      {
        type: 'added',
        items: [
          'Editor de Roles: los grupos de permisos ahora se pueden colapsar/expandir individualmente (útil con más de 40 permisos), y hay un buscador que filtra por nombre del permiso, clave técnica (ej. "accounts.manage") o módulo (ej. "Facturación"), tanto en la vista de solo lectura como en el formulario de crear/editar rol.',
        ],
      },
    ],
  },
  {
    version: '1.24.9',
    date: '2026-09-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Un operador ya puede ver los clientes en Cuentas. Antes, aunque su rol tenía el permiso "accounts.view", una regla de seguridad de la base de datos (RLS) solo dejaba ver la tabla de cuentas a Admin/Super Admin, así que la lista le aparecía vacía. Los operadores siguen sin poder crear/editar/eliminar cuentas — solo consultarlas.',
        ],
      },
    ],
  },
  {
    version: '1.24.8',
    date: '2026-09-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Dashboard /emergency: se corrigió un error que impedía cargar los eventos ARC activos en el mapa ("400 Bad Request" / "no se encontró relación entre arc_events y cid_codes"). La consulta intentaba usar una relación que fue eliminada intencionalmente el 26 de agosto (para que paneles Hikvision con códigos CID no estándar no fallaran al guardarse). Ahora la descripción del código CID se obtiene del mismo diccionario local que ya usan Centro ARC y Auditoría.',
        ],
      },
    ],
  },
  {
    version: '1.24.7',
    date: '2026-09-14',
    sections: [
      {
        type: 'added',
        items: [
          'Dashboard web: cuando un usuario inicia sesión con la contraseña temporal por defecto (asignada al crear su cuenta), ahora se le pide obligatoriamente definir una contraseña propia antes de poder usar el resto del sistema — igual que ya sucedía en las apps móviles.',
          'Nueva opción "Cambiar contraseña" en la barra lateral (debajo del nombre del usuario), disponible para cualquier operador en cualquier momento, sin depender de un correo de restablecimiento.',
        ],
      },
    ],
  },
  {
    version: '1.24.6',
    date: '2026-09-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'Usuarios: al crear un usuario con un correo que ya existe, ya no aparece el error genérico "Edge Function returned a non-2xx status code" — ahora se muestra el mensaje real ("Ya existe un usuario registrado con el correo...").',
        ],
      },
    ],
  },
  {
    version: '1.24.5',
    date: '2026-09-14',
    sections: [
      {
        type: 'changed',
        items: [
          'Barra lateral reorganizada: "Alarmas" ahora abre Centro ARC directamente (sin submenú). "Cuentas" agrupa en pestañas Cuentas / Usuarios / Roles. Nueva entrada "Solicitudes" agrupa en pestañas Solicitudes de unión / Acceso ASI. "Historial" se movió a la sección de sistema, junto a Auditoría.',
          'Dispositivos: el panel de alarmas físicas se renombró de "Alarmas" a "Páneles" para no confundirlo con el nuevo acceso directo a Centro ARC.',
          'Centro ARC: la prioridad de eventos ahora agrupa Pánico y Robo/Intrusión como prioridad 1 (antes iban separados en 1 y 2), incluyendo el CID 103 (el código real de disparo de zona interior/perimetral que usan los paneles Hikvision AX PRO) — antes caía incorrectamente en "otras alarmas".',
          'Auditoría: la tabla ahora muestra la columna "Zona" y cada fila se puede abrir para ver el detalle completo del evento (zona, código de usuario, quién reconoció/resolvió, mensaje crudo del panel, etc.).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Centro ARC: los eventos de la Zona 0 (la primera zona reportada por el panel) ya no se muestran como "-" en la Cinta Histórica en Tiempo Real, el detalle de evento, ni en Auditoría — era un error de código que trataba el número de zona 0 como "sin zona".',
        ],
      },
    ],
  },
  {
    version: '1.24.4',
    date: '2026-09-14',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil (ASI Security): corregido un cierre inesperado al abrir la app en algunos dispositivos, causado por una dependencia faltante en el build de producción.',
          'App móvil: el aviso de "Permitir ubicación en segundo plano" ya no desaparece de inmediato — ahora se queda en pantalla el tiempo necesario para que el usuario pueda leerlo y confirmar antes de continuar al ajuste del sistema.',
          'App móvil: el aviso de permiso de ubicación ya no vuelve a aparecer cada vez que se abre la app — solo se muestra una vez después de instalarla.',
        ],
      },
    ],
  },
  {
    version: '1.24.3',
    date: '2026-09-11',
    sections: [
      {
        type: 'fixed',
        items: [
          'ARC: paneles recién vinculados podían quedar sin recibir eventos de armado/desarmado/alarma porque Hikvision (HPP) no los incluía automáticamente en la suscripción de eventos. Ahora el sistema detecta y corrige esto solo cada vez que se sincronizan paneles.',
          'Zonificación: se corrigió la clasificación del tipo de zona para que use el dispositivo real (sensor de movimiento, magnético, etc.) en vez del modo de armado del panel — antes esto causaba que casi todas las zonas aparecieran marcadas como "revisar" incorrectamente, y que los magnéticos configurados en modo "seguimiento" aparecieran como tipo "Otro".',
          'App móvil: unificado el diseño de la tarjeta de panel — un panel de una sola área ahora se ve igual que uno con varias áreas (antes tenían botones con nombres distintos).',
        ],
      },
      {
        type: 'changed',
        items: [
          'Zonificación: los íconos de dispositivo volvieron a un estilo más simple y grande (insignia circular de color), y ahora se traduce al español el tipo de dispositivo reportado por Hikvision (ej. "Contacto magnético" en vez de "Magnetic Contact").',
          'Zonificación: cuando un sitio tiene más de un panel, cada uno se muestra en su propio recuadro con borde para diferenciarlos claramente.',
        ],
      },
    ],
  },
  {
    version: '1.24.2',
    date: '2026-09-11',
    sections: [
      {
        type: 'added',
        items: [
          'Zonificación: botón "Sincronizar con panel" que trae el tipo de dispositivo real (sensor de movimiento, magnético, etc.), el tipo de zona configurado en el panel y el área/partición real directamente desde Hikvision — ya no se adivina a partir de los códigos CID.',
          'Zonificación: cada zona ahora muestra un ícono según el tipo de dispositivo real (movimiento, magnético de puerta, incendio, pánico, etc.).',
          'Zonificación: las zonas ahora se agrupan por área/partición del panel (ej. "Área 1", "Área 2") cuando el sitio tiene más de una.',
          'Zonificación: si un dispositivo fue eliminado del panel físico, la próxima sincronización lo marca como "Eliminado del panel"; también se puede borrar manualmente desde el dashboard.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Zonificación: el número de zona se muestra tal como lo reporta el panel (0, 1, 2...), sin desplazamiento artificial.',
        ],
      },
    ],
  },
  {
    version: '1.24.1',
    date: '2026-09-11',
    sections: [
      {
        type: 'changed',
        items: [
          'Zonificación: el croquis de instalación y la tabla de zonas ahora se muestran lado a lado en dos columnas, y los botones de vincular/desactivar/desvincular panel se movieron arriba para acceso más rápido.',
          'Zonificación: el número de zona mostrado ahora coincide con el que ves en el panel/app de Hikvision (antes empezaba en 0 por cómo lo reporta Hikvision internamente).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'ARC: corregido un segundo código CID mal etiquetado — el 103, que Hikvision usa como código genérico de zona interior/perimetral (movimiento, magnéticos de puerta), aparecía clasificado como "Médica" en Zonificación. Se confirmó que los códigos médicos reales (100-102) nunca se usan en ningún panel.',
          'Zonificación: el tipo de una zona ya no cambia cada vez que llega un evento de restauración o de apertura/cierre — solo se reclasifica con eventos de alarma real, evitando que zonas como sensores de puerta "parpadearan" entre tipos distintos.',
        ],
      },
    ],
  },
  {
    version: '1.24.0',
    date: '2026-09-11',
    sections: [
      {
        type: 'added',
        items: [
          'Nueva sección "Zonificación" en la pestaña Paneles de cada sitio: muestra qué dispositivo/sensor está conectado a cada zona del panel, con su tipo (Pánico, Incendio, Médica, Perimetral, etc.) y el último evento recibido. Se llena automáticamente con la información que ya reporta Hikvision — no requiere configuración manual.',
          'Corrección manual de zonas: si el nombre o tipo de una zona no coincide con la realidad, ahora se puede editar directamente desde el dashboard sin depender de la app de Hikvision.',
          'Nuevo campo "Croquis de instalación" en cada sitio: permite subir el plano/croquis de cómo quedó instalada la alarma (ubicación de sensores, paneles y cámaras), para que el técnico lo consulte de inmediato al dar mantenimiento.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'ARC: los eventos del botón de pánico físico de Hikvision ya no aparecen como "Alarma Térmica" — se corrigió la descripción del código CID 129, que en los paneles AX PRO corresponde al botón de pánico físico, no a una alarma térmica.',
        ],
      },
    ],
  },
  {
    version: '1.23.4',
    date: '2026-09-03',
    sections: [
      {
        type: 'fixed',
        items: [
          'App ASI Patrol (Android): el mapa, el perfil, alertas e historial ya no se meten debajo de la barra de estado ni del área de notificaciones en Android 15+.',
          'App ASI Patrol: el teclado ya no tapa los campos de correo y contraseña al iniciar sesión; el formulario ahora se desliza suavemente hacia arriba mientras el teclado está abierto y vuelve a centrarse al cerrarlo.',
          'App ASI Patrol: el botón de retroceso de Android ya no cierra la app al ver el detalle de una alerta desde el historial — ahora regresa correctamente a la pantalla anterior.',
        ],
      },
    ],
  },
  {
    version: '1.23.3',
    date: '2026-09-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil iOS: corregido el espaciado del texto en campos de correo y botones recortados en registro, confirmación de correo, recuperación de contraseña y vínculo de cuenta.',
          'App móvil Android: ícono de ASI Security renovado en todas las versiones compatibles.',
          'App móvil: al iniciar sesión, ahora se diferencia claramente entre una cuenta inexistente y una contraseña incorrecta.',
          'Eliminación de cuenta: tras eliminarla, puedes registrarte de nuevo con el mismo correo como una cuenta totalmente nueva, sin acceso a información anterior.',
        ],
      },
      {
        type: 'security',
        items: [
          'No se permite eliminar una cuenta mientras tenga pagos pendientes.',
        ],
      },
    ],
  },
  {
    version: '1.23.2',
    date: '2026-08-29',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil iOS: crash en simulador iPad corregido (acelerómetro no disponible en simulador).',
          'App móvil: botón "Crear cuenta" ahora muestra el texto correctamente en iOS.',
        ],
      },
    ],
  },
  {
    version: '1.23.1',
    date: '2026-08-27',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil iOS: notificaciones FCM ahora funcionan. Firebase no se inicializaba en iOS (faltaba FirebaseApp.configure() y GoogleService-Info.plist en el proyecto Xcode).',
          'App móvil iOS: corregido crash al activar biometría en dispositivos Face ID (iPhone 17e). Faltaba NSFaceIDUsageDescription en Info.plist.',
          'App móvil iOS: notificaciones ahora aparecen como banners. El permiso se pedía como "provisional" (entrega silenciosa).',
          'App móvil: texto de biometría actualizado a genérico — aplica a Face ID y huella.',
          'App móvil iOS: filtros de Dispositivos (Todos / Cámaras / Alarmas) ahora caben en pantallas pequeñas con scroll horizontal.',
        ],
      },
    ],
  },
  {
    version: '1.23.0',
    date: '2026-08-27',
    sections: [
      {
        type: 'added',
        items: [
          'Panel AX PRO: omisión de zonas en fallo desde el dashboard (bypass). Si una zona está abierta, fuera de rango o con tamper, aparece un banner con botones "Omitir zona y activar total/parcial". La zona se omite temporalmente y el sistema se activa.',
          'Panel AX PRO: el gateway ahora verifica zonas offline (sensor desconectado) antes de armar y muestra su nombre y razón exacta en el mensaje de error.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Dashboard: el drawer de panel ahora muestra primero "Áreas del panel", luego info del panel, luego "Acceso de clientes".',
          'Dashboard: al intentar desarmar durante el exit delay (conteo de salida del panel), aparece un banner amber explicativo en lugar de error silencioso.',
          'Dashboard: armar desde el web ahora actualiza el estado del panel en tiempo real inmediatamente (sin esperar el evento CID del MQ).',
          'Dashboard: armar/desarmar desde la web ahora envía notificación FCM correctamente (await faltante en notifyArmEvent).',
          'App móvil: el banner de alarmas en inicio ahora muestra zonas únicas en alarma en lugar de conteo de eventos (ej. "Sensor Movimiento · Zona 1" en vez de "50 alarmas de panel").',
          'App móvil: el historial de alarmas ya no muestra eventos de restauración.',
          'App móvil: el historial de alarmas deduplica eventos del mismo sensor/zona dentro de ventana de 5 minutos.',
          'App móvil: el contador regresivo para cancelar SOS por sacudida/caída ahora aparece correctamente cuando la pantalla está encendida.',
          'App móvil: ya no aparece "Nueva Notificación" cuando llega un mensaje FCM sin contenido visible.',
          'Notificaciones FCM: formato mejorado con separador · y títulos más cortos. "Sensor fuera de rango" renombrado a "Sensor desconectado".',
        ],
      },
    ],
  },
  {
    version: '1.22.9',
    date: '2026-08-24',
    sections: [
      {
        type: 'added',
        items: [
          'App móvil iOS: slide-to-SOS ahora verifica permiso de ubicación antes de enviar (estilo Waze). Si el permiso está bloqueado, muestra instrucciones específicas según si es el toggle global o el permiso de la app.',
          'App móvil iOS: shake-to-SOS habilitado. Agita el teléfono 3 veces en 1.5 segundos para enviar alerta de emergencia automáticamente.',
          'Notificaciones FCM anti-flood: sensores que disparan múltiples eventos en ráfaga ahora generan solo 1 notificación por ventana de 30 segundos, por dispositivo y zona. Eventos críticos siempre llegan sin restricción.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'App móvil iOS: el slider de SOS ya no queda trabado al final al cancelar el diálogo de ubicación.',
          'App móvil iOS: el mensaje de "ubicación desactivada" ahora diferencia correctamente entre servicios globalmente desactivados y permiso denegado.',
        ],
      },
    ],
  },
  {
    version: '1.22.7',
    date: '2026-08-12',
    sections: [
      {
        type: 'added',
        items: [
          'Integración de paneles de alarma por TCP: receptor Contact ID en el gateway (puerto 3010) compatible con Intelbras AMT, DSC, Hikvision AX PRO, Paradox y cualquier panel con protocolo Contact ID estándar.',
          'Receptor TCP: auto-descubre el IP del panel en su primera conexión — no requiere configuración manual en Supabase ni en el MikroTik.',
          'Control bidireccional de paneles (tab "Control directo (TCP)" en el dashboard): activar/desactivar el sistema desde el dashboard usando protocolo nativo 0xe7 por puerto 9009. Requiere que el panel sea alcanzable desde el gateway (VPN).',
          'Configurador MikroTik: nueva opción "Panel de alarma en el .rsc" — incluye instrucciones de configuración del receptor Contact ID (IP 10.0.0.1, puerto 3010) en el archivo .rsc generado.',
          'Configurador MikroTik: capacidad VPN expandida a 10.0.0.0/8 (~1,048,575 sitios). Previamente limitado a /16 (~4,094 sitios). Los sitios existentes no se ven afectados.',
          'Registro de panel TCP: wizard de 3 pasos en el módulo de Alarmas para registrar paneles de cualquier marca (Intelbras, Hikvision, DSC, Paradox, Genérico) con código de cuenta auto-generado.',
          'Filtrado de eventos de prueba periódica: los heartbeats supervisory (CID 601-609) ya no se insertan en el historial — solo actualizan el estado ONLINE del panel. Eliminados ~140,000 eventos acumulados.',
        ],
      },
      {
        type: 'changed',
        items: [
          'App móvil ASI Security: notificaciones push de alarmas ahora usan canal "asi_alert_urgent" (IMPORTANCE_HIGH) → se muestran como heads-up notification (slide desde arriba) con vibración, incluso con la pantalla apagada.',
          'App móvil ASI Security: reducción del consumo de batería en segundo plano — precisión GPS cambiada a MEDIUM, distancia mínima de 100m, heartbeat de ubicación cada 10 min (antes 2 min).',
          'App móvil ASI Security: cámaras en pantalla de Ubicaciones ahora muestran snapshots cada 5 segundos en lugar de streaming HLS continuo. El video en vivo solo se activa al abrir la cámara en pantalla completa. Reduce consumo de datos en ~80%.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'FCM push notifications: eventos CRITICAL/ALARM ya usan canal "asi_alert_urgent" con IMPORTANCE_HIGH. Antes usaban canal "default" (silencioso, sin heads-up) y los operadores no notaban las alertas hasta abrir la app.',
        ],
      },
    ],
  },
  {
    version: '1.22.6',
    date: '2026-08-10',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security v1.18.5: notificaciones repetitivas de ubicación corregidas — el sistema ya no envía avisos redundantes cada 2.5 minutos relacionados con el servicio de ubicación en segundo plano.',
          'App móvil ASI Security v1.18.5: el banner de "Armando..." desaparece correctamente cuando el panel confirma el armado, incluso si la conexión en tiempo real presentó algún problema.',
          'App móvil ASI Security v1.18.5: los mensajes de alarma en notificaciones push ya no dicen "Robo" — ahora muestran descripciones claras como "Movimiento detectado - zona interior" o "Alarma activada - zona perimetral".',
          'App móvil ASI Security v1.18.5: la pantalla de inicio ya no aparece brevemente durante 3-5 segundos al abrir la app después de una suspensión profunda.',
          'App móvil ASI Security v1.18.5: al intentar armar con una zona abierta, el mensaje ahora dice "No se puede armar el sistema" con instrucciones claras.',
          'App móvil ASI Security v1.18.5: el audio de las cámaras en modo HD ya funciona correctamente.',
        ],
      },
    ],
  },
  {
    version: '1.22.5',
    date: '2026-08-09',
    sections: [
      {
        type: 'added',
        items: [
          'Web: permisos granulares por usuario — los administradores ahora pueden asignar permisos adicionales a cada operador/admin sin necesidad de crear un rol nuevo. Los permisos se suman a los del rol base.',
          'App móvil ASI Security v1.18.5: la app ya no muestra el onboarding al abrir después de una suspensión profunda — el estado de cuenta se cachea localmente.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Web: permisos faltantes en roles del sistema corregidos — operator ahora puede ver y crear usuarios; admin/super_admin pueden ver y confirmar cobros.',
          'App móvil ASI Security v1.18.5: la alerta de Bluetooth desactivado ya no aparece cada 15 segundos — máximo una vez cada 4 horas.',
          'App móvil ASI Security v1.18.5: al intentar armar un panel con alarma activa, el mensaje ahora dice "Alarma activa" en vez de mostrar el código técnico en inglés.',
          'App móvil ASI Security v1.18.5: typo corregido en onboarding ("Instalaá" → "Instala").',
          'App móvil ASI Security v1.18.5: usuarios en modo de prueba ven ahora un banner claro que explica qué funciones se habilitarán con la suscripción.',
          'App móvil ASI Security v1.18.5: el flujo de permisos al primer inicio quedó unificado en un solo modal de Términos y Condiciones.',
        ],
      },
    ],
  },
  {
    version: '1.22.4',
    date: '2026-08-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'ARC: las notificaciones de alarma crítica (pánico, incendio, robo) ahora llegan al teléfono del cliente con prioridad máxima, patrón de vibración y pantalla completa aunque la app esté en segundo plano, igual que las alertas SOS.',
        ],
      },
    ],
  },
  {
    version: '1.22.3',
    date: '2026-08-05',
    sections: [
      {
        type: 'added',
        items: [
          'ARC: los eventos ahora muestran la hora real en que ocurrieron en el sitio (reloj del panel), no la hora en que llegaron al sistema. Si el panel estuvo sin señal y reportó eventos en diferido, la hora mostrada refleja cuándo sucedió realmente la alarma.',
          'ARC: indicador visual \u26a1 en la lista de eventos cuando un evento llegó con retraso significativo (panel reconectado tras pérdida de señal). El tooltip muestra cuántos minutos tardó en llegar.',
          'ARC: exportación CSV ahora incluye dos columnas de tiempo: "Fecha/Hora (evento)" (hora del panel) y "Fecha/Hora (recibido)" (hora de llegada al sistema).',
          'ARC: en el detalle de evento ahora se muestra "Ocurrió hace X" y "Recibido hace X" por separado para claridad operativa.',
        ],
      },
    ],
  },
  {
    version: '1.22.2',
    date: '2026-08-04',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security v1.18.5: el botón MWB02 ya detecta correctamente el reset natural del dispositivo (doble tap) y vuelve a estar listo para disparar sin necesidad de reiniciar la app.',
          'App móvil ASI Security v1.18.5: se eliminó el banner naranja "Botón en modo SOS" que aparecía tras un disparo — la experiencia es ahora limpia y sin interrupciones visuales.',
          'App móvil ASI Security v1.18.5: corregido error de bundle que impedía arrancar la app tras un disparo MWB02 (identificador duplicado en ble-button).',
        ],
      },
    ],
  },
  {
    version: '1.22.1',
    date: '2026-08-03',
    sections: [
      {
        type: 'fixed',
        items: [
          'Facturación: las facturas de respuesta de patrulla ya no cuentan para la suspensión automática de cuenta. Solo las facturas mensuales de suscripción y activaciones de plan pueden activar la suspensión.',
          'Facturación: el tipo de factura ahora queda registrado explícitamente (suscripción, activación de plan, despacho de patrulla) para mayor claridad y trazabilidad.',
          'App móvil ASI Security v1.18.4: los propietarios de cuentas pueden ver sus facturas pendientes desde "Mis Facturas" aunque la cuenta esté suspendida por falta de pago.',
          'App móvil ASI Security v1.18.4: el banner "Servicio suspendido" ahora muestra el botón "Ver mis facturas" para ir directamente a pagarlas.',
          'App móvil ASI Security v1.18.4: los botones de armar/desarmar de cada sitio ya no se bloquean por la suspensión de una cuenta diferente.',
          'App móvil ASI Security v1.18.4: los botones de armar/desarmar se restauran automáticamente cuando la app vuelve al primer plano tras haber estado en segundo plano durante una reactivación de cuenta.',
        ],
      },
    ],
  },
  {
    version: '1.22.0',
    date: '2026-07-29',
    sections: [
      {
        type: 'added',
        items: [
          'Dashboard /settings → Operaciones: toggle para activar o desactivar el mini-mapa en el ticket SOS de la app móvil. Permite ocultarlo temporalmente sin eliminarlo del código.',
          'Dashboard /users: pestaña "Eliminados" para ver usuarios dados de baja. Los usuarios eliminados ya no bloquean la lista principal.',
          'Facturación FEL: indicador visual "Último día" y "Plazo vencido" en cada factura certificada. El sistema bloquea la anulación directa pasado el día siguiente a la emisión e indica el proceso extemporáneo (PBX INFILE 2458-2458 opción 2).',
        ],
      },
      {
        type: 'changed',
        items: [
          'Dashboard /users: eliminar un usuario ahora hace soft-delete (oculta de la lista y bloquea el acceso) en lugar de fallar por restricciones de FK cuando el usuario tiene tickets históricos.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security v1.18.3: el mapa en el ticket SOS ahora carga correctamente en producción (SHA-1 de la clave de firma de Google Play registrada en la API key de Google Maps).',
          'Dashboard /users: el mensaje de error al eliminar un usuario ahora muestra la causa real en lugar de "Edge Function returned a non-2xx status code".',
        ],
      },
    ],
  },
  {
    version: '1.21.1',
    date: '2026-07-06',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security: el botón BLE B10 ahora responde correctamente a presses consecutivos. Se corrigió un bug donde la ventana de detección de phantom se extendía indefinidamente durante ciclos de reposo/activación, bloqueando todas las alertas subsecuentes. Ahora el botón queda listo ~3 segundos después de despertar del reposo.',
        ],
      },
    ],
  },
  {
    version: '1.21.0',
    date: '2026-07-05',
    sections: [
      {
        type: 'added',
        items: [
          'App móvil ASI Security: soporte para botón BLE Minew D15N con detección de doble presión sin falsas alarmas. El D15N utiliza el trigger Eddystone feaa (configurado con BeaconSET+) para detectar doble tap con cero phantoms. Patrón de uso: si el botón muestra «En reposo», presionar 1× para despertar y luego doble presión para SOS.',
          'App móvil ASI Security: el estado del botón BLE muestra instrucciones específicas según el modelo (B10: presión doble rápida; D15N: doble presión para SOS con guía de wakeup).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security: el botón B10 ya detecta correctamente el estado de reposo profundo (sleep/wake) y reanuda el escaneo BLE automáticamente cuando el device vuelve a estar disponible.',
        ],
      },
    ],
  },
  {
    version: '1.20.0',
    date: '2026-06-26',
    sections: [
      {
        type: 'fixed',
        items: [
          'Dashboard /emergency: las alarmas de panel ya se clasifican correctamente según el código CID — incendio (FIRE), intrusión (INTRUSION) o pánico (PANIC). Antes todos los eventos se creaban como PÁNICO.',
          'Dashboard /emergency: la tarjeta de alarma ahora muestra el nombre del sitio como identificador principal. Antes aparecía solo el número de cuenta porque el ticket no tenía site_id almacenado.',
          'App móvil ASI Security: el banner «Armando…» ya desaparece al reanudar la app después de una suspensión profunda (Android Doze Mode), aunque el evento de confirmación haya llegado mientras la app estaba suspendida.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Dashboard /emergency: la tarjeta de alarma de panel muestra el ícono de edificio con el nombre del sitio en negrita, el detalle de zona y código CID, y botones de cierre rápido («Falsa alarma» / «Resolver») directamente en la tarjeta — sin necesidad de abrir el panel de detalle.',
          'Dashboard /emergency: el detalle de una alarma de panel ya no muestra el chat de mensajes (no aplica para alertas originadas en panel), quedando solo las acciones de proceso: asignar patrullero, resolver o cancelar como falsa alarma.',
        ],
      },
    ],
  },
  {
    version: '1.19.0',
    date: '2026-06-22',
    sections: [
      {
        type: 'added',
        items: [
          'App móvil ASI Security: flujo de confirmación de alarma para paneles Hikvision AX PRO. Botones por área que cambian a «Confirmar» cuando hay alarma activa en esa zona, y banner «Silenciar» en la tarjeta del panel.',
          'Gateway: endpoint POST /api/hpp/panels/:serial/clear-alarm con soporte para silenciar todas las áreas (subsysId=0) o una zona específica.',
          'Gateway: detección de estado ALARM en tiempo real vía HPP MQ para actualizar el campo status del panel y mostrar el banner en la app.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'App móvil: pre-verificación de zonas antes de armar — si hay una puerta o ventana abierta, el armado se bloquea al instante (~15 s) mostrando el nombre exacto de la zona. Ya no hay que esperar 60–85 s para recibir un error sin información.',
          'App móvil: botón «Confirmar» sólo aparece en áreas que están armadas; áreas desarmadas no muestran la opción de confirmar aunque el panel esté en alarma.',
          'App móvil: «Silenciar alarma» ahora solo silencia (clearAlarm), el panel permanece armado. «Desarmar» es una acción separada. Si el panel rechaza el disarm por alarma activa, ofrece «Confirmar y Desarmar» juntos.',
          'App móvil: banner «Silenciar» del panel llama clearAlarm en todos los subsistemas (no solo el primero), evitando el caso donde la alarma estaba en el área Interior (subsysId=2) y se limpiaba solo Exterior (subsysId=1).',
          'Gateway MQ: el restablecimiento de un sensor (puerta que se cierra, PIR que deja de detectar) ya no limpia el estado ALARM del panel. La alarma solo se limpia con clearAlarm explícito o con un evento CID de disarm.',
          'Gateway MQ: ventana de supresión post-clearAlarm de 5 segundos para evitar que eventos CID en cola re-activen el banner inmediatamente después de silenciar.',
          'Gateway: el evento disarm (CID open/close) ahora también limpia el status a ONLINE, resolviendo el caso donde el panel queda en ALARM después de desarmar sin haber confirmado.',
        ],
      },
    ],
  },
  {
    version: '1.18.5',
    date: '2026-06-12',
    sections: [
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security v1.16.5: corregido crash al abrir la app en release. El bundle incluía react-native@0.76.9 junto a 0.85.3 por la resolución de módulos de pnpm en el monorepo, causando que TurboModuleRegistry fallara al encontrar PlatformConstants (SIGABRT). Solucionado en la configuración de Metro.',
        ],
      },
    ],
  },
  {
    version: '1.18.4',
    date: '2026-06-04',
    sections: [
      {
        type: 'fixed',
        items: [
          'Gateway ONVIF: la generación de claves WireGuard ahora usa spawnSync con stdin en lugar de /bin/bash (incompatible con Alpine Linux). Esto resuelve el bug donde la clave privada llegaba vacía al MikroTik tras el import del script .rsc.',
          'Código de invitación: error «edge function returned a non-2xx status code» al canjear un código desde la app. La función redeem-invite no estaba desplegada en producción.',
        ],
      },
      {
        type: 'added',
        items: [
          'Script deploy-full.ps1 ahora actualiza automáticamente el gateway EC2: git pull, rebuild del contenedor Docker y verificación/instalación del cron de WireGuard — todo desde un solo comando de despliegue.',
          'Paneles HPP: pestañas «Sin cuenta» / «En cuenta» en la lista de paneles para facilitar la gestión.',
          'Paneles HPP: buscador en el diálogo de vinculación de sitio, y opción de agregar un panel a un sitio ya existente.',
          'Paneles HPP: acción «Vincular sitio existente» desde AccountDetailPage para asociar sitios HPP a una cuenta directamente.',
          'Paneles HPP: columnas de sitio y cuenta visibles en la tabla de paneles asignados.',
        ],
      },
    ],
  },
  {
    version: '1.18.3',
    date: '2026-06-01',
    sections: [
      {
        type: 'fixed',
        items: [
          'Flujo de restablecimiento de contraseña completamente funcional desde la app móvil: nueva página /reset-password con estados loading → confirmar → formulario → completado.',
          'El enlace del correo de recuperación es ahora seguro contra escáneres: la URL de Supabase se codifica en base64url para que los bots que hacen pre-fetch no consuman el token OTP de un solo uso.',
          'Verificación del token in-place sin recargar la página, evitando que el evento PASSWORD_RECOVERY se pierda por el ciclo de vida del componente.',
          'Corregido el spinner «Guardando...» infinito al enviar la nueva contraseña: la UI avanza al estado final inmediatamente y el cierre de sesión ocurre en segundo plano. Se añadió try/catch/finally para que los errores se muestren correctamente.',
        ],
      },
      {
        type: 'added',
        items: [
          'App móvil ASI Security v1.16.5 (build 81): release APK + AAB generados y distribuidos.',
        ],
      },
    ],
  },
  {
    version: '1.18.2',
    date: '2026-05-28',
    sections: [
      {
        type: 'added',
        items: [
          'Modo de programación configurable para la facturación automática: «Primer día hábil del mes» (recomendado), «Día fijo del mes» o «Manual» (sin auto-ejecución, solo desde Facturación masiva).',
          'Edge function `billing-cron-run` calcula automáticamente el primer lunes–viernes del mes en zona Guatemala cuando está en modo «primer día hábil» (si el día 1 cae sábado/domingo, espera al lunes).',
        ],
      },
      {
        type: 'changed',
        items: [
          'La UI de Configuración de facturación explica claramente cuándo va a ejecutarse el cron según el modo elegido, y oculta los campos irrelevantes (día fijo se oculta en modo «primer día hábil» y «manual»).',
        ],
      },
    ],
  },
  {
    version: '1.18.1',
    date: '2026-05-28',
    sections: [
      {
        type: 'changed',
        items: [
          'Menú lateral reorganizado: «Cobros», «Pagos (QPay)», «Facturación (FEL)», «Facturación masiva», «Contratos», «Productos» y «Configuración de facturación» ahora cuelgan de un solo grupo expandible «Facturación y cobros».',
          'Aviso PCI visible también en la cabecera del módulo de Contratos (antes sólo aparecía en Configuración de facturación).',
          'Scrollbars del dashboard rediseñadas (más finas, semi-transparentes, hover suave). Reemplazan el scrollbar nativo del sistema operativo.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Script `setup-billing-cron.sql`: corregido error `42601` (`ON CONFLICT` no aplica a `vault.create_secret`). Ahora hace `DELETE` previo del secret y luego `create_secret`, manteniendo la idempotencia.',
        ],
      },
    ],
  },
  {
    version: '1.18.0',
    date: '2026-05-28',
    sections: [
      {
        type: 'added',
        items: [
          'Catálogo de productos / servicios facturables: nuevo menú «Productos» en Administración para definir códigos, descripciones (con variables {month}, {year}, {site_name}, {account_name}, {nit}), precios base y unidad de medida.',
          'Contratos / suscripciones recurrentes por cuenta y por sitio: nuevo menú «Contratos» que vincula cada cuenta a uno o varios productos con frecuencia (mensual/trimestral/anual), día de facturación (1–28), precio override y fechas de inicio/fin.',
          'Modo de facturación configurable por cuenta: consolidada (todos los sitios en una sola factura) o por sitio (una factura por cada sitio).',
          'Facturación masiva: nuevo menú «Facturación masiva» para generar facturas borrador de las suscripciones que vencen en un periodo, con vista previa, selección manual y bitácora de lotes ejecutados.',
          'Cron de facturación automática: nuevo menú «Config. facturación» permite activar el cron, elegir día del mes y hora local (Guatemala) en los que se generan las facturas borrador del periodo en curso.',
          'Flujo de aprobación de facturas: ahora las facturas se crean en estado «borrador». Sólo un administrador puede aprobarlas, lo que registra quién aprobó (`approved_by`) y cuándo (`approved_at`) antes de emitir el FEL.',
          'Bloqueo de NIT no validado para FEL: el sistema bloquea emisión de FEL si el NIT del receptor no ha sido validado previamente contra INFILE (excepto Consumidor Final), para evitar consumir certificaciones en NITs inexistentes. Configurable desde «Config. facturación».',
          'Cuando se valida un NIT desde la ficha de la cuenta, ahora se guarda la fecha de validación y el nombre legal devuelto por INFILE.',
          'Idempotencia: el sistema impide facturar dos veces la misma suscripción en el mismo periodo a nivel de base de datos.',
        ],
      },
      {
        type: 'changed',
        items: [
          'El botón «Emitir FEL» en facturas borrador ahora dice «Aprobar y emitir»: aprueba la factura y emite el DTE en un solo paso, dejando registro de quién la aprobó.',
        ],
      },
      {
        type: 'security',
        items: [
          'Política PCI explícita: ASI Security no almacena datos de tarjetas. Se removió la opción de auto-cobro recurrente en contratos. El cliente debe ingresar manualmente su tarjeta en cada pago (la pasarela procesa, nosotros solo recibimos confirmación). Aviso visible en «Config. facturación» y en el formulario de contratos.',
          'Catálogo de prueba sembrado: Monitoreo residencial, Monitoreo comercial, Patrullaje, Almacenamiento en la nube, Soporte técnico mensual.',
        ],
      },
    ],
  },
  {
    version: '1.17.3',
    date: '2026-05-27',
    sections: [
      {
        type: 'changed',
        items: [
          'Push de «Factura emitida» ahora se envía únicamente al titular (owner) de la cuenta. Miembros secundarios (managers/workers) ya no reciben este aviso.',
          'Validación de NIT: cuando INFILE responde «Credenciales no registradas» (servicio Consulta Receptores no contratado en DEMO), el botón Validar ahora muestra un mensaje claro indicando que la consulta SAT no está disponible y que se puede emitir igualmente. El certificador rechazará si el NIT no existe.',
        ],
      },
    ],
  },
  {
    version: '1.17.2',
    date: '2026-05-27',
    sections: [
      {
        type: 'added',
        items: [
          'Notificación push al cliente cuando se emite una factura electrónica: tan pronto INFILE certifica el DTE (automáticamente al aprobar un depósito o al cobrar con tarjeta, o de forma manual desde la web), todos los miembros activos de la cuenta reciben un push «Factura emitida» con número de factura, monto y enlace al PDF.',
          'App móvil ASI Security v1.16.0: en «Mis facturas» cada factura ya emitida muestra un botón «Ver factura PDF» que abre el documento certificado directamente.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'App móvil ASI Security v1.16.0: al entrar a «Mi suscripción» desde Perfil, ya no se muestra el aviso «Actualiza tu plan para acceder a esta función». Ahora el header dice «Mi suscripción» y el aviso de upgrade solo aparece cuando el sistema requiere realmente cambiar de plan.',
        ],
      },
    ],
  },
  {
    version: '1.17.1',
    date: '2026-05-27',
    sections: [
      {
        type: 'added',
        items: [
          'Validación de NIT en línea al emitir FEL: en el diálogo «Emitir FEL» de Cuentas → Facturación, el campo NIT ahora tiene un botón «Validar» que consulta el padrón de SAT vía INFILE y autocompleta automáticamente el nombre fiscal del receptor. CF se reconoce como Consumidor Final sin necesidad de consultar.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Edge function `fel-consult-nit`: ahora es env-aware (DEMO/PROD) y resuelve credenciales y endpoint de consulta según `system_settings.fel_environment`, igual que el resto del stack FEL.',
        ],
      },
    ],
  },
  {
    version: '1.17.0',
    date: '2026-05-27',
    sections: [
      {
        type: 'added',
        items: [
          'Facturación electrónica obligatoria en todos los pagos (cumplimiento SAT). Al pagar por depósito o tarjeta, el cliente ya no decide si quiere o no factura: el sistema siempre emite un DTE FEL. Si el cliente no edita los datos, la factura sale a CONSUMIDOR FINAL (CF).',
          'Datos de facturación editables al subir comprobante de depósito: el cliente puede ajustar NIT, razón social, dirección y correo. Pre-rellenados con los datos fiscales de la cuenta cuando existen, con CF como fallback automático.',
          'Auto-emisión de FEL al cobrar con tarjeta (QPay): cuando el cobro QPay queda aprobado, el sistema dispara automáticamente la emisión del DTE usando el NIT y datos capturados en la pantalla de pago.',
          'Auto-emisión de FEL al aprobar un depósito bancario desde Centro de Cobros: el cobrador ya no tiene que recordar emitir la factura aparte.',
          'Emisión manual de factura desde la web: en Cuentas → Facturación cada factura cobrada muestra un botón «Emitir FEL» que abre un diálogo con el receptor editable (NIT/nombre/dirección/correo) pre-rellenado desde los datos fiscales de la cuenta. Una vez emitida, aparece enlace directo al PDF certificado.',
          'Centro de Cobros: cada solicitud de pago muestra un distintivo con el NIT del receptor para que el cobrador procese conscientemente.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Edge function `fel-emit-dte`: ahora acepta llamadas autenticadas con service-role (para invocación desde otras edge functions) y un parámetro opcional `receptor` que sobreescribe los datos fiscales por defecto de la cuenta. Cadena de resolución: override → `account.fel_*` → `account.billing_*` → `account.name/address` → CF.',
          'Edge functions `confirm-payment` y `qpay-charge`: al aprobar un pago disparan la emisión FEL de forma no bloqueante — si FEL falla, el pago igualmente se completa y el operador puede reintentar manualmente desde la web.',
          'Tabla `invoice_payment_attempts`: nuevas columnas `requires_invoice`, `receptor_nit`, `receptor_name`, `receptor_address`, `receptor_email` para persistir los datos fiscales capturados al momento del pago.',
        ],
      },
    ],
  },
  {
    version: '1.16.0',
    date: '2026-05-27',
    sections: [
      {
        type: 'added',
        items: [
          'Facturación electrónica (FEL) — integración con INFILE: el sistema ahora puede firmar y certificar DTEs directamente contra el certificador oficial. Flujo verificado end-to-end con DEMO.',
          'Configuración → Facturación (FEL): nueva sección para super-administradores en /admin/fel que permite alternar entre el entorno DEMO (pruebas) y Producción (facturas reales ante SAT) con un solo clic, sin redeploy.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Edge function `fel-emit-dte`: migrada al flujo correcto de INFILE (Sign → Certify), con endpoints `signer-emisores.feel.com.gt` y `certificador.feel.com.gt/fel/certificacion/v2/dte/`. Las credenciales y URLs ahora se resuelven por ambiente desde `system_settings.fel_environment` (prefijos `FEL_DEMO_*` / `FEL_PROD_*`).',
        ],
      },
    ],
  },
  {
    version: '1.15.2',
    date: '2026-05-24',
    sections: [
      {
        type: 'added',
        items: [
          'App (cliente): desbloqueo con huella dactilar — la app solicita autenticación biométrica al reanudarse tras 5 minutos de inactividad. Activable/desactivable desde Perfil.',
          'App (cliente): foto de perfil — los usuarios pueden tomar o subir una foto desde la galería; se muestra en el encabezado de Perfil.',
          'App (cliente): pantalla dedicada de configuración "SOS por agitación y caída" con sensibilidad (Baja/Media/Alta) y cuenta regresiva (5/10/15s).',
        ],
      },
      {
        type: 'changed',
        items: [
          'App (cliente): pantalla de Perfil reorganizada — secciones más claras (Apariencia, Notificaciones, Seguridad, Facturación), eliminado el desorden visual.',
          'App (cliente): corregido solapamiento del contenido con la barra de notificaciones de Android en todas las pantallas.',
          'App (cliente): "Ícono en el mapa" ahora muestra subtítulo explicando que es cómo apareces en el mapa del operador durante alertas activas.',
        ],
      },
    ],
  },
  {
    version: 'Hotfix 2026-05-24',
    date: '2026-05-24',
    sections: [
      {
        type: 'fixed',
        items: [
          'Base de datos: corrección crítica de recursión infinita en políticas RLS de la tabla profiles (error PostgreSQL 42P17). Causaba: nombre "Usuario" en el home, historial de alertas vacío, y alertas encoladas offline aunque hubiera señal.',
          'App (cliente): botón "Aceptar" en la vista completa de Política de Privacidad ahora muestra correctamente el texto (estilo flex:1 en contexto de columna causaba renderizado incorrecto).',
          'App (cliente): alertas enviadas con WiFi/datos LTE ya no van a la cola offline. La recursión RLS hacía que process-alert fallara con 500, lo que activaba el fallback de cola.',
        ],
      },
      {
        type: 'added',
        items: [
          'App (cliente): nueva opción "Mi suscripción" en la pantalla de Perfil > Facturación para ver y cambiar el plan de servicio directamente.',
        ],
      },
    ],
  },
  {
    version: 'Mobile-client 1.15.0',
    date: '2026-05-24',
    sections: [
      {
        type: 'added',
        items: [
          'App (cliente): pantalla "Actualiza tu plan" ahora es funcional — el botón muestra el precio del plan y "Activar / Mejorar / Cambiar" según corresponda y al tocarlo genera la factura y abre la pantalla de pago con tarjeta.',
          'Backend: nueva edge function `activate-plan` que crea/actualiza la suscripción y genera la factura. Soporta tres flujos: activación nueva (factura por mes completo), upgrade (factura prorrateada por los días restantes del ciclo) y downgrade (cambio programado al final del ciclo, sin cobro inmediato).',
          'Base de datos: trigger `apply_plan_on_invoice_paid` que activa automáticamente la suscripción cuando la factura asociada se marca como pagada (cubre pago con tarjeta QPay y depósito bancario por igual).',
        ],
      },
    ],
  },
  {
    version: 'Seguridad BD 2026-05-20',
    date: '2026-05-20',
    sections: [
      {
        type: 'security',
        items: [
          'Base de datos: vista v_my_account_join_requests convertida a security_invoker para que respete las políticas RLS de las tablas subyacentes (antes era SECURITY DEFINER — advertencia crítica de Supabase Advisor).',
          'Base de datos: nueva política RLS en profiles permite a dueños/gestores de cuenta ver el perfil de los usuarios que solicitan unirse a su cuenta.',
        ],
      },
    ],
  },
  {
    version: 'Mobile-client 1.14.0',
    date: '2026-05-20',
    sections: [
      {
        type: 'fixed',
        items: [
          'App (cliente): pantalla "Recordatorio de armado" ya no se queda cargando indefinidamente al entrar (deadlock del ActivityIndicator corregido).',
          'App (cliente): consulta de sitios vía client_sites ahora usa el alias correcto del join de PostgREST (site.is_active en lugar de alarm_sites.is_active).',
        ],
      },
      {
        type: 'changed',
        items: [
          'App (cliente): tarjetas de paneles en Dispositivos rediseñadas — siempre muestran los 3 botones (Cierre Total / Perimetral / Desarmar) con color verde cuando el estado está activo.',
          'App (cliente): los íconos de estadísticas en Inicio (Dispositivos, Cámaras, Alertas) ahora son tappables y navegan a la sección correspondiente.',
        ],
      },
    ],
  },
  {
    version: 'Hotfix 2026-05-18',
    date: '2026-05-18',
    sections: [
      {
        type: 'fixed',
        items: [
          'App (cliente): textos en la pantalla de planes (Paywall) que aparecían en negro sobre fondo oscuro ahora se muestran correctamente.',
          'App (cliente): el ícono del botón SOS ahora gira mientras se procesa el envío de la alerta.',
          'Dashboard web: el favicon ahora se muestra como PNG (triángulo ASI).',
        ],
      },
    ],
  },
  {
    version: 'Hotfix 2026-05-19',
    date: '2026-05-19',
    sections: [
      {
        type: 'changed',
        items: [
          'QPay: el switch Sandbox ↔ Producción en /admin/payments ahora cambia TODO con un clic (URL + credenciales). Antes había que actualizar los secrets a mano en Supabase para que el toggle funcionara correctamente.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Pagos con tarjeta: las facturas ahora sí cambian a "Pagada" cuando QPay aprueba el cobro. Antes, un error interno dejaba la factura en "Pendiente" aunque el cobro se había procesado correctamente.',
          'Pagos con tarjeta: si por alguna razón no se puede marcar la factura como pagada, la app ahora muestra el error real en lugar de un falso "aprobado".',
        ],
      },
    ],
  },
  {
    version: '1.13.9 (mobile)',
    date: '2026-05-19',
    sections: [
      {
        type: 'fixed',
        items: [
          'Alertas: el botón para cancelar una alerta ya no aparece en alertas del historial que ya estaban cerradas o finalizadas.',
        ],
      },
    ],
  },
  {
    version: '1.13.8 (mobile)',
    date: '2026-05-19',
    sections: [
      {
        type: 'fixed',
        items: [
          'Pago con tarjeta: los datos de facturación (nombre, apellido, correo, teléfono) vuelven a precargarse automáticamente desde tu perfil al abrir la pantalla. Si tu perfil estaba aún cargando, ya no se quedan vacíos.',
        ],
      },
    ],
  },
  {
    version: '1.10.6 (web)',
    date: '2026-05-19',
    sections: [
      {
        type: 'added',
        items: [
          'Aviso automático de pagos vencidos: el sistema ahora detecta cada hora las facturas vencidas y muestra un popup persistente al titular de la cuenta con CTA «Ver y pagar». Si el cliente lo descarta, se vuelve a mostrar hasta 12 horas después; si paga, el aviso se cierra solo.',
        ],
      },
    ],
  },
  {
    version: '1.10.5 (web)',
    date: '2026-05-19',
    sections: [
      {
        type: 'added',
        items: [
          'Cobros: nueva acción «Reembolsar» para pagos QPay aprobados. Pide motivo, devuelve el dinero a la tarjeta original (sin volver a capturar datos) y anula la factura. Disponible para administradores y super-administradores.',
          'Configuración → Pagos (QPay): nueva sección para super-administradores que permite alternar entre el entorno Sandbox (pruebas) y Producción (cobros reales) de QPay Pro, sin necesidad de redeploy.',
        ],
      },
      {
        type: 'security',
        items: [
          'Política de Privacidad actualizada (mayo de 2026): se documenta de forma explícita que ASI Security NO almacena datos de tarjeta (PAN, vencimiento, CVV) y que el procesamiento se delega en QPay Pro, procesador certificado PCI-DSS. Se añade la Sección 5.bis específica de pagos con tarjeta y reembolsos.',
        ],
      },
    ],
  },
  {
    version: '1.10.4 (web) + 1.13.7 (mobile-client)',
    date: '2026-05-19',
    sections: [
      {
        type: 'changed',
        items: [
          'Pago con tarjeta: la tarjeta animada ahora queda fija en la parte superior de la pantalla y solo hace scroll el formulario (datos de tarjeta + facturación). Así siempre ves la tarjeta voltearse cuando enfocas el CVV, aunque hayas bajado a llenar la dirección.',
          'Ingresos → sección Facturación: se eliminó el aviso de «se habilitará en la Fase 9» — el módulo ya está activo (Cobros + QPay).',
        ],
      },
    ],
  },
  {
    version: '1.13.6 (mobile-client)',
    date: '2026-05-19',
    sections: [
      {
        type: 'fixed',
        items: [
          'Pago con tarjeta (QPay): la edge function `qpay-charge` interpretaba el formato antiguo de respuesta (`x_response_status`) cuando QPay Pro ya devuelve `result` / `responseCode` / `idTransaction`. Esto causaba que la app marcara como «rechazada» una transacción que QPay sí cobró. Se ajustó el parser para reconocer ambos formatos y se cambió `AUTH_ONLY` por `AUTH_CAPTURE` para capturar el cobro inmediatamente.',
          'Tarjeta animada: ahora muestra el logo oficial de Corporación A.S.I. y el nombre correcto (antes decía «ASI Corporación»).',
        ],
      },
    ],
  },
  {
    version: '1.10.3 (web) + 1.13.5 (mobile-client)',
    date: '2026-05-19',
    sections: [
      {
        type: 'added',
        items: [
          'Pago con tarjeta (QPay Pro): nueva pantalla en la app del cliente con tarjeta animada que gira al enfocar el CVV (mostrando el reverso con la banda magnética y el código). Detecta la marca (Visa / Mastercard / Amex) en tiempo real, valida el número con Luhn y muestra el logo «ASI Corporación». Se conecta al endpoint server-to-server `qpay-charge` (sandbox) y, en aprobación, marca la factura como pagada y envía push al titular de la cuenta.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Cobros y Comunicaciones: ancho y espaciado igualados a Ingresos (`container mx-auto p-6 space-y-4`). Antes Cobros se desbordaba a los bordes y la separación vertical era mayor que en el resto del panel.',
        ],
      },
    ],
  },
  {
    version: '1.10.2 (web)',
    date: '2026-05-19',
    sections: [
      {
        type: 'fixed',
        items: [
          'Cobros y Mis facturas: los websockets de Supabase Realtime ya estaban suscritos en el front, pero el back no publicaba las tablas de facturas/pagos. Se agregó `invoice_payment_attempts` e `invoices` a la publicación `supabase_realtime` con REPLICA IDENTITY FULL — ahora la lista del operador y la app se actualizan automáticamente.',
          'Notificación push de pago aprobado/rechazado: ahora se envía únicamente al titular de la cuenta (`role=\'owner\'`) y ya no al resto de miembros (operadores, técnicos, trabajadores).',
          'Configuración: pequeño ajuste de espaciado en el encabezado para igualar al resto de páginas.',
        ],
      },
    ],
  },
  {
    version: '1.10.1 (web)',
    date: '2026-05-19',
    sections: [
      {
        type: 'changed',
        items: [
          'Cobros: rediseño completo con pestañas (Pendientes / Aprobados / Rechazados / Todos), búsqueda por cuenta/factura/banco/referencia, filtro por rango de fechas, ordenamiento por fecha o monto y paginación de 20 en 20.',
          'Cobros: los botones “Aprobar” y “Rechazar” ahora abren un diálogo propio con resumen de la factura y campo para motivo del rechazo. Se eliminaron los `confirm`/`prompt` del navegador.',
          'Cobros: la vista se actualiza en tiempo real cuando cambia cualquier intento de pago o factura (sin necesidad de refrescar manualmente).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Cobros y Configuración: padding inconsistente con el resto del dashboard (`p-4 lg:p-6` → `p-6 space-y-6`).',
        ],
      },
    ],
  },
  {
    version: '1.10.0 (web)',
    date: '2026-05-18',
    sections: [
      {
        type: 'added',
        items: [
          'Cobros (F9): nuevo módulo “Cobros” en el menú lateral para revisar los comprobantes de depósito bancario que los clientes envían desde la app móvil. Cada comprobante muestra cuenta, factura, monto, banco, referencia y la foto/PDF en un visor; un solo clic aprueba (marca la factura como pagada) o rechaza con motivo.',
          'Eliminación definitiva de cuentas: el botón “Eliminar cuenta” en la lista de cuentas ahora hace borrado real (incluye facturas, intentos de pago, suscripciones y, opcionalmente, sitios/cámaras/paneles). Pensado para limpiar cuentas de prueba que antes solo podían desactivarse.',
        ],
      },
    ],
  },
  {
    version: '1.13.4 (mobile-client)',
    date: '2026-05-19',
    sections: [
      {
        type: 'changed',
        items: [
          'Mis facturas: el diseño ahora usa el mismo fondo oscuro y colores del resto de la app.',
          'Mis facturas: la pantalla se refresca automáticamente al regresar de “Enviar comprobante” — ya no aparece el botón “Enviar comprobante” cuando el envío ya se hizo.',
          'Mis facturas: se actualiza en tiempo real cuando el operador aprueba o rechaza el comprobante (Realtime sobre facturas + intentos de pago).',
          'Enviar comprobante: ajustes visuales para que se integre con el tema oscuro consistente.',
        ],
      },
      {
        type: 'added',
        items: [
          'Notificación push cuando el operador aprueba o rechaza un comprobante de pago. Incluye el número de factura y el motivo del rechazo si aplica.',
        ],
      },
    ],
  },
  {
    version: '1.13.3 (mobile-client)',
    date: '2026-05-18',
    sections: [
      {
        type: 'added',
        items: [
          'Mis facturas: nueva pantalla en Perfil → “Mis facturas” que lista todas las facturas de tus cuentas con estado, monto y vencimiento.',
          'Comprobante de depósito: en cualquier factura pendiente puedes tomar una foto de tu boleta de depósito bancario, capturar el banco/referencia/fecha y enviarla. El equipo de cobros la revisa y, al aprobarla, la factura cambia a “Pagada” automáticamente.',
        ],
      },
    ],
  },
  {
    version: '1.9.1 (web)',
    date: '2026-05-18',
    sections: [
      {
        type: 'changed',
        items: [
          'Cuentas demo: ahora funcionan al 100% igual que cuentas reales (alertas, cámaras, paneles, todo según su plan). El toggle “Cuenta de demostración” pasa a ser una etiqueta de analítica para excluirla de métricas de ingresos y conteo de clientes pagados.',
          'Para apagar el servicio a un cliente (falta de pago, fin de demo, baja), usa el botón existente “Desactivar cuenta” — ahora sí bloquea todas las alertas en el backend, no solo en UI.',
        ],
      },
    ],
  },
  {
    version: '1.9.0 (web)',
    date: '2026-05-18',
    sections: [
      {
        type: 'added',
        items: [
          'Cuentas demo desde el panel: nuevo toggle “Cuenta de demostración” en Editar cuenta. Cualquier admin puede convertir una cuenta en demo (badge naranja “DEMO” en el detalle) sin tener que tocar Supabase — útil para entregar demos a prospectos.',
          'Al marcar una cuenta como demo, todos sus miembros activos quedan automáticamente bloqueados para enviar alertas reales (SOS / pánico). El cambio se propaga al instante vía trigger en base de datos.',
          'Cámaras: nuevo switch “Visible en demo pública” en el panel de detalle de cámara. Permite curar exactamente qué cámaras ven los usuarios que entran a la demo pública desde la app móvil.',
        ],
      },
    ],
  },
  {
    version: '1.13.0 (mobile-client)',
    date: '2026-05-18',
    sections: [
      {
        type: 'added',
        items: [
          'Demo pública: nueva opción en el onboarding “Solo quiero ver cómo funciona”. El usuario entra como invitado a una cuenta de demostración sin tarjeta ni datos adicionales para conocer la app antes de contratar.',
          'Los usuarios en modo demo pueden ver las cámaras públicas, el mapa de patrullas y notificaciones de ejemplo. Las alertas reales (SOS, pánico) están deshabilitadas para no generar tickets al ARC.',
          'Al intentar disparar una alerta real desde la demo, la app muestra el modal de upsell invitando a crear una cuenta propia con 7 días gratis.',
        ],
      },
    ],
  },
  {
    version: '1.12.2 (mobile-client)',
    date: '2026-05-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Comunicaciones — Banner: ya se ve correctamente arriba de la pantalla (antes quedaba escondido detrás del navegador).',
          'Comunicaciones — Pop-up: ya no se cierra solo a los pocos milisegundos. Permanece visible hasta que tocas “Cerrar” o el botón de acción. Además ya soporta varios pop-ups en secuencia.',
          'Campana de notificaciones: el contador “+9” ahora se actualiza al instante al borrar notificaciones desde la bandeja (antes quedaba colgado).',
          'Creación de cuenta de prueba: arreglado un error que rompía el alta de cualquier cuenta nueva (el trigger de límite de miembros usaba mayúsculas equivocadas).',
          'Mensajes de error en pantalla: dejaron de ser genéricos (“Edge Function returned a non-2xx status code”). Ahora muestran detalles útiles cuando algo falla en el alta de cuentas.',
        ],
      },
    ],
  },
  {
    version: '1.12.0 (mobile-client)',
    date: '2026-05-17',
    sections: [
      {
        type: 'added',
        items: [
          'Comunicaciones — Pop-up y Banner: la app ASI Security ya recibe y muestra los mensajes que envías desde el dashboard (Comunicaciones). Pop-up sale como modal a pantalla completa la primera vez que el usuario abre la app; Banner queda fijo arriba hasta que el usuario lo cierra o expire.',
          'Notificaciones (campana): la campana en la pantalla de inicio ahora muestra un contador con los mensajes no leídos y abre una bandeja completa de notificaciones. Tocar un mensaje lo marca como leído y ejecuta la acción del CTA si la tiene.',
          'Acciones CTA soportadas: abrir URL externa (open_url:…) o navegar a otra pantalla de la app (open_screen:…).',
        ],
      },
    ],
  },
  {
    version: '1.8.4 (web)',
    date: '2026-05-17',
    sections: [
      {
        type: 'fixed',
        items: [
          'Plan Básico: la descripción del plan en /admin/plans ya no dice “CRA”, ahora dice “ARC” (se actualiza la fila en la base de datos al aplicar la migración).',
          'Comunicaciones — Historial: la paginación ahora es siempre visible (muestra “Página 1 de 1 · N envío(s)” incluso con pocos registros).',
        ],
      },
      {
        type: 'changed',
        items: [
          'Planes: límite de miembros ahora se valida en la base de datos. PRO permite 3 miembros activos por cuenta (1 titular + 2), PREMIUM permite 5 (1 titular + 4). Intentar agregar uno más falla con un error claro.',
        ],
      },
      {
        type: 'added',
        items: [
          'TODO: actualización forzada de la app de patrulleros usando Google Play In-App Updates en modo “flexible” (NO bloqueante — las emergencias no descansan).',
        ],
      },
    ],
  },
  {
    version: '1.8.3 (web)',
    date: '2026-05-17',
    sections: [
      {
        type: 'changed',
        items: [
          'Audiencia "Todos": ahora envía solo a clientes (titulares y miembros con rol cliente). Ya no incluye patrulleros ni personal interno — para eso usa el filtro "Por rol".',
          'Audiencia "Por cuenta": el envío llega únicamente al titular de la cuenta (no a todo el equipo).',
          'Selector de cuentas rediseñado con buscador en vivo (por nombre o número de cuenta), chips de selección y "Seleccionar todos / Limpiar". Reemplaza al selector de usuarios sueltos.',
          'Planes: la opción "Solo llamada al CRA" se renombró a "Solo llamada al ARC" (Alarm/Alert Receiver Center) para alinearse con la terminología oficial de ASI.',
        ],
      },
      {
        type: 'added',
        items: [
          'Historial de comunicaciones: buscador por título o mensaje + paginación de 10 en 10.',
          'Historial: botón de eliminar envío disponible solo para Super Admin.',
          'Tip en el módulo: para obligar a los patrulleros a actualizar la app, envía un Pop-up al rol "Patrulleros" con el botón "Abrir Play Store".',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Notificaciones FCM duplicadas que no llegaban al volver a enviarse: ahora cada broadcast lleva un tag único, evitando que Android colapse mensajes con título/cuerpo idénticos.',
        ],
      },
    ],
  },
  {
    version: '1.8.2 (web)',
    date: '2026-05-17',
    sections: [
      {
        type: 'added',
        items: [
          'Comunicaciones: ahora puedes enviar Push, Pop-up y Banner al mismo tiempo (selecciona los canales que quieras y se crea un envío por cada uno).',
          'Plantillas de mensajes (CRUD): crea, edita, elimina y reutiliza formatos predefinidos (ideal para el recordatorio mensual de facturación).',
          'Selector de plantilla en "Nuevo envío": carga el título, mensaje y botón de una plantilla con un clic.',
          'Búsqueda de usuarios por nombre o email en el selector de audiencia, con selección múltiple y chips para quitar destinatarios.',
          'Permisos en tiempo real: el módulo Comunicaciones se actualiza automáticamente (WebSocket) sin necesidad de refrescar.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Padding corregido en la página /admin/communications (las tarjetas ya no aparecen cortadas contra los bordes).',
          'Resumen de audiencia ahora muestra correctamente el número de usuarios seleccionados manualmente.',
        ],
      },
    ],
  },
  {
    version: '1.8.1 (web)',
    date: '2026-05-17',
    sections: [
      {
        type: 'added',
        items: [
          'Módulo Comunicaciones (admin): nueva página /admin/communications para enviar Push (FCM), Pop-ups y Banners a los usuarios.',
          'Selector de audiencia amigable: todos los usuarios, por rol, por plan o por cuenta (selección múltiple).',
          'Historial en tiempo real de comunicaciones enviadas, con número real de destinatarios alcanzados.',
          'Permisos nuevos: communications.view / communications.send (asignados a admin y super_admin).',
          'Página de Roles actualizada: ahora muestra los permisos de Cuentas, Planes y Comunicaciones que faltaban en el catálogo.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Sidebar de sistema: nueva entrada "Comunicaciones" con ícono de megáfono.',
        ],
      },
    ],
  },
  {
    version: '1.8.0 (web)',
    date: '2026-05-17',
    sections: [
      {
        type: 'added',
        items: [
          'Planes de suscripción (admin): nueva página /admin/plans para crear, editar, activar/desactivar, publicar/ocultar y administrar el catálogo de planes (TRIAL/BASIC/PRO/PREMIUM). Cada cambio de precio queda registrado automáticamente en el historial.',
          'Revenue dashboard: nueva página /admin/revenue con MRR, ARR proyectado, suscripciones activas y en prueba, distribución por plan y resumen de facturación (cuando F9 esté activa).',
          'Permisos nuevos: plans.view / plans.manage / revenue.view. Admin tiene view; super_admin puede gestionar planes.',
          'Sidebar: nuevas entradas "Planes" y "Revenue" en la sección de sistema.',
        ],
      },
    ],
  },
  {
    version: '1.11.7',
    date: '2026-05-16',
    sections: [
      {
        type: 'added',
        items: [
          'Catálogo de planes (TRIAL / BASIC / PRO / PREMIUM) con precios en GTQ, histórico de precios y características por plan (despacho de patrulla, videollamada, cámaras).',
          'Límite de prueba gratuita: la prueba de 7 días ahora incluye UNA única respuesta de patrulla (SOS / PÁNICO). Al intentar enviar una segunda alerta, la app móvil muestra una pantalla "Actualiza tu plan" con los planes disponibles.',
          'Plan BASIC (call-only): los usuarios con plan BASIC no pueden despachar patrulla y verán la pantalla de upsell si intentan enviar SOS/PÁNICO. La llamada al CRA sigue disponible.',
          'PaywallScreen móvil: nueva pantalla modal que se abre cuando un usuario alcanza el límite de su plan, mostrando los planes recomendados con sus precios y características.',
          'Tab "Invitaciones" dentro del detalle de cuenta web (antes solo accesible por URL directa).',
          'Entrada "Solicitudes" en la barra lateral web para acceder rápidamente a las solicitudes de unión pendientes.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Eliminar usuario desde la UI: ahora limpia automáticamente las referencias en account_members, client_locations, alert_recipients y otras tablas con FK CASCADE antes de borrar al usuario. Si el usuario es el único owner de una cuenta sin miembros adicionales, también borra la cuenta. Cuando hay datos operativos asociados (tickets, paneles, sitios) el sistema devuelve el mensaje real del error indicando que conviene desactivar en lugar de eliminar.',
        ],
      },
    ],
  },
  {
    version: '1.11.6',
    date: '2026-05-16',
    sections: [
      {
        type: 'added',
        items: [
          'Onboarding wizard en app móvil cliente: al iniciar sesión por primera vez (sin cuenta asignada) se muestra un asistente con 3 opciones — (A) crear cuenta nueva e iniciar prueba gratuita de 7 días, (B) unirse por código de invitación, o (C) solicitar unirse buscando al dueño de la cuenta por correo electrónico.',
          'Invitaciones por código: los administradores web pueden generar códigos de invitación por cuenta con número máximo de usos y fecha de vencimiento. Disponible en Cuentas → Invitaciones.',
          'Solicitudes de unión: cuando un cliente solicita unirse buscando al owner por email, el owner recibe una notificación push. El web dashboard tiene la nueva página "Solicitudes de Unión" para aprobar o rechazar solicitudes pendientes.',
          'Prueba gratuita 7 días: al crear una cuenta nueva desde la app, se activa automáticamente una suscripción de prueba con 1 alerta de patrulla incluida.',
          'Cuentas web — desactivar / reactivar / eliminar: cada fila del listado de Cuentas ahora tiene un menú de acciones (⋮) con las opciones "Desactivar" (soft, recuperable), "Reactivar" y "Eliminar" (permanente, con doble confirmación + verificación del número de cuenta). Si la cuenta tiene sitios, suscripciones o facturas asociadas, la eliminación falla y el sistema sugiere desactivarla en su lugar. Nuevo filtro "Mostrar inactivas" para ver cuentas desactivadas.',
        ],
      },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-05-16',
    sections: [
      {
        type: 'added',
        items: [
          'Geocercas de zonas inseguras (tipo DANGER) — la página de Geocercas ahora permite dibujar polígonos directamente sobre el mapa de Google con la herramienta de dibujo, definir categoría, severidad (baja/media/alta/crítica), radio de proximidad, horario de actividad (hora inicio/fin) y mensaje personalizado de notificación. Los clientes móviles reciben un push al entrar dentro del horario activo.',
          'App móvil cliente — alertas de zona insegura: cuando el cliente entra a una zona DANGER activa dentro de su horario, recibe una notificación push “⚠️ Zona insegura” con el mensaje configurado por el operador. Cada zona tiene un enfriamiento de 10 minutos para evitar repeticiones.',
          'Recordatorio de armar el panel (geocerca tipo ARM_REMINDER) — el cliente puede personalizar título, cuerpo y horario del recordatorio desde la app, y se respetan las preferencias por usuario sobre el default del sitio.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Página de Geocercas rediseñada: mapa interactivo con dibujo de polígonos, edición visual de vértices, lista lateral con todas las geocercas y formulario detallado para cada una. Soporta los 4 tipos: PATROL_AREA, SITE_PERIMETER, ARM_REMINDER y el nuevo DANGER.',
        ],
      },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-05-11',
    sections: [
      {
        type: 'added',
        items: [
          'Mapa de emergencias — nueva tarjeta de detalle de cuenta/sitio: al hacer clic en un marcador del mapa o en una fila del panel “Cuentas” se despliega una vista enriquecida con teléfono clicable (compatible con Webex Click-to-Call), botón a Google Maps, KPIs (paneles online, paneles armados, eventos en 24 h, cámaras), eventos abiertos, lista de contactos por prioridad y historial reciente de eventos.',
          'Indicador “Cuenta abierta / cerrada” basado en el estado de armado de los paneles: si algún panel reportó heartbeat en los últimos 30 minutos y está desarmado, la cuenta se marca como abierta (apertura en tiempo); si todos los paneles están armados, como cerrada.',
          'Favicon de marca (triángulo navy de ASI) reemplaza el icono por defecto de Vite.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Número de cuenta visible en cada fila del panel “Cuentas” como pill azul independiente (#ASI-2026-NNNN), que ya no se trunca con el nombre. Si el sitio no tiene cuenta vinculada se muestra “sin cuenta”.',
          'El número de cuenta ahora se obtiene del registro canónico en accounts (con fallback a la copia denormalizada en alarm_sites), por lo que las cuentas creadas vía el módulo Accounts ahora aparecen correctamente.',
          'Hacer clic en una fila o marcador de sitio ya no abre automáticamente el evento ARC más reciente: ahora muestra primero la tarjeta de detalle del sitio, desde donde el operador puede abrir el evento específico que necesite.',
        ],
      },
    ],
  },
  {
    version: '1.9.10',
    date: '2026-05-11',
    sections: [
      {
        type: 'changed',
        items: [
          'Mapa de emergencias — buscador de cuentas estilo DataTables: acepta varios términos separados por espacio (todos deben coincidir), busca sin distinguir mayúsculas/acentos, y ahora también encuentra por nombre de cliente además de número de cuenta, sitio y dirección.',
          'Cada fila de la lista muestra el número de cuenta como prefijo monoespaciado (#1234) junto al título "Cliente · Sitio" para identificar al instante a qué cuenta pertenece.',
        ],
      },
    ],
  },
  {
    version: '1.9.9',
    date: '2026-05-11',
    sections: [
      {
        type: 'changed',
        items: [
          'Mapa de emergencias — los sitios ahora se identifican como “Cuenta · Sitio” en la lista lateral y en el tooltip del mapa, para distinguir clientes con nombres de sitio iguales.',
          'Marcadores con la paleta de marca: azul marino para sitios sin eventos activos o con patrulla en camino, rojo cuando tienen un evento abierto, y ámbar para sitios prioritarios con 3 o más eventos simultáneos.',
        ],
      },
    ],
  },
  {
    version: '1.9.8',
    date: '2026-05-11',
    sections: [
      {
        type: 'fixed',
        items: [
          'Mapa de emergencias — la pestaña "Cuentas" mostraba 0 cuentas y el mensaje "No hay cuentas registradas" aunque sí había sitios activos. La carga de sitios se desacopló de la carga de eventos ARC: si la consulta de eventos fallaba (p. ej. relación PostgREST con caché desactualizada), también se descartaban los sitios. Ahora ambas se cargan de forma independiente y los sitios aparecen siempre.',
          'Sitios sin valor de "is_active" definido (importaciones antiguas) ahora también se incluyen en el roster del mapa de emergencias.',
        ],
      },
    ],
  },
  {
    version: '1.9.7',
    date: '2026-05-08',
    sections: [
      {
        type: 'added',
        items: [
          'Importación de cámaras — el sistema detecta cuando intentas importar una cámara que ya está registrada en otro sitio o cuenta (mismo número de serie) y muestra un diálogo para confirmar el desvínculo antes de moverla. Evita streams huérfanos compitiendo por el mismo identificador.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Gateway — al importar una cámara nueva sin sub-RTSP propio (Uniarch y similares), ahora el stream SD se transcodea desde el principal con audio incluido. Antes el SD se quedaba mudo o sin video.',
          'Cámara "Demo Segundo Nivel" deshabilitada manualmente para liberar el stream que estaba colisionando con la cámara real "Oficina" en Residencia Principal.',
        ],
      },
    ],
  },
  {
    version: '1.9.6',
    date: '2026-05-08',
    sections: [
      {
        type: 'fixed',
        items: [
          'App cliente — PTT (push-to-talk) restaurado al flujo REST original que sí llega a la cámara (mantén presionado, suelta para enviar). El flujo WebRTC anterior conectaba pero no propagaba el audio.',
        ],
      },
    ],
  },
  {
    version: '1.9.5',
    date: '2026-05-08',
    sections: [
      {
        type: 'fixed',
        items: [
          'App cliente — audio en SD recuperado: al cambiar HD↔SD se reinicia la bandera “sin audio” (antes la cámara nunca volvía a intentar audio si HD había fallado primero).',
          'App cliente — PTT muestra mensaje claro cuando la cámara rechaza el back-channel ONVIF.',
        ],
      },
    ],
  },
  {
    version: '1.9.4',
    date: '2026-05-08',
    sections: [
      {
        type: 'fixed',
        items: [
          'App cliente — ahora cualquier error de reproducción en HD dispara el fallback automático a SD (antes el filtro era demasiado estricto y la cámara Uniarch quedaba mostrando error de video).',
          'App cliente — nuevo indicador "SIN AUDIO" en el reproductor: cuando la cámara no entrega audio, ahora aparece una insignia amarilla en vez de fallar silenciosamente.',
          'App cliente — botón de hablar (PTT) ahora muestra mensaje real cuando la cámara no soporta back-channel de audio (antes quedaba como si nada hubiera pasado).',
        ],
      },
    ],
  },
  {
    version: '1.9.3',
    date: '2026-05-08',
    sections: [
      {
        type: 'added',
        items: [
          'App cliente — nuevo filtro por tipo de dispositivo en la pantalla Dispositivos: chips Todos / Cámaras / Paneles con conteo en vivo.',
          'App cliente — fallback automático HD→SD: si la cámara no puede reproducir en HD (problema común en Uniarch), baja a SD en lugar de mostrar error.',
          'App cliente — modal de Términos y Política de Privacidad rediseado como tarjeta pequeña con botón "Ver completo" para el texto íntegro.',
        ],
      },
      {
        type: 'changed',
        items: [
          'App cliente — las pantallas del reproductor de cámara ya no fuerzan modo horizontal; ahora rotan libremente con el sensor del teléfono.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'App cliente — el botón de hablar (PTT) ya no cierra la app cuando falla el micrófono o WebRTC; muestra un estado de error y permite reintentar.',
        ],
      },
    ],
  },
  {
    version: '1.9.2',
    date: '2026-05-08',
    sections: [
      {
        type: 'added',
        items: [
          'Detalle de alerta enriquecido: cuando una alerta proviene de un panel de alarma, el panel derecho ahora muestra la cuenta completa (número, titular, sitio) y destaca la clave verbal de verificación para que el operador la use antes de despachar.',
          'Procesamiento en lote desde el centro de monitoreo: cuando un panel dispara una ráfaga de alertas, ahora podés marcar todo el grupo como "Falsa alarma" o "Resuelto" con un solo botón (con confirmación de doble clic) en lugar de hacerlo una por una.',
          'Agrupación inteligente en la cola de alertas: los tickets de paneles ahora se agrupan por sitio/cuenta con el nombre real, no como "Cliente" genérico.',
        ],
      },
      {
        type: 'changed',
        items: [
          'La tarjeta "Cliente" del detalle de alerta se oculta cuando la alerta proviene de un panel (no hay cliente móvil asociado).',
          'La dirección del ticket usa la dirección del sitio como respaldo cuando no se capturó una propia.',
        ],
      },
    ],
  },
  {
    version: '1.9.1',
    date: '2026-05-08',
    sections: [
      {
        type: 'fixed',
        items: [
          'Pestaña "Cuentas" recortada en el panel derecho del centro de monitoreo: las cuatro pestañas ahora usan un layout compacto (icono + etiqueta + badge) que cabe sin truncarse.',
          'La pestaña "Cuentas" abre por defecto mostrando todas las cuentas registradas en lugar de quedar vacía. El filtro "Solo sitios con eventos activos" sigue disponible y se puede activar manualmente.',
          'Las cuentas sincronizadas desde HPP que aún no tienen coordenadas geográficas también aparecen ahora en la lista de Cuentas (antes quedaban ocultas porque solo se cargaban las geocodificadas). En el mapa solo se siguen mostrando las que sí tienen lat/lng.',
          'Mensajes de estado vacío más claros en la lista de Cuentas según si no hay cuentas registradas, no hay eventos activos o no hay coincidencias con el filtro.',
        ],
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-05-08',
    sections: [
      {
        type: 'added',
        items: [
          'Centro de monitoreo unificado — los sitios de cuentas con paneles de alarma o cámaras ahora aparecen en el mapa de emergencias junto con clientes y patrullas. Los marcadores cambian de color según el estado del sitio (gris=ok, ámbar=advertencia, rojo=alarma, azul=patrulla en camino).',
          'Nueva pestaña "Cuentas" en el panel derecho del centro de monitoreo: lista los sitios ordenados por urgencia con un resumen del último evento abierto y permite buscar por nombre, número de cuenta o dirección.',
          'Despacho automático de pánico desde paneles: cuando un panel ARC envía un código de emergencia configurado como auto-dispatch (1120 pánico, 1110 incendio, etc.) se crea automáticamente un ticket CRÍTICO y se asigna a la patrulla más cercana sin intervención del operador.',
          'Despacho manual desde un evento ARC: el operador puede acusar, marcar como falsa alarma o despachar patrulla a cualquier evento abierto desde el detalle del evento en el centro de monitoreo.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Eventos ARC ahora se sincronizan en tiempo real al dashboard sin necesidad de recargar — alta latencia (>1 s) entre que el panel envía la alarma y aparece en el mapa eliminada.',
        ],
      },
    ],
  },
  {
    version: '1.8.29',
    date: '2026-05-07',
    sections: [
      {
        type: 'added',
        items: [
          'Móvil cliente — talkback en tiempo real: el audio del micrófono ya llega a la bocina de la cámara en ~100 ms (antes 2-4 s). Se usa WebRTC directo a go2rtc en lugar del flujo anterior de grabar, subir y transcodificar.',
          'Móvil cliente — flujo de recuperación de contraseña: enlace "¿Olvidaste tu contraseña?" en la pantalla de ingreso. Envía un código OTP de 6 dígitos al correo y permite elegir una nueva contraseña sin contactar soporte.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Móvil cliente — miniaturas en vivo para cámaras Uniarch ya muestran video real de go2rtc en lugar de captura estática.',
          'Móvil cliente — botón de talkback ya aparece en cámaras Uniarch (supports_two_way_audio se actualiza automáticamente al registrar cámara).',
        ],
      },
    ],
  },
  {
    version: '1.8.28',
    date: '2026-05-07',
    sections: [
      {
        type: 'fixed',
        items: [
          'Móvil cliente — mensajes de error de cámaras reemplazados por texto amigable (sin códigos técnicos ni strings de ExoPlayer).',
          'Móvil cliente — si después de 30 segundos no se encuentra patrullero disponible, se muestra mensaje claro en lugar de girar indefinidamente.',
          'Web — botón "Cancelar alerta" ahora aparece también en alertas con estado ASIGNADA (antes solo en PENDIENTE).',
          'Móvil — clientes ahora pueden cancelar sus propias alertas desde la app (política RLS corregida).',
        ],
      },
      {
        type: 'added',
        items: [
          'Móvil cliente y patrulla — Términos de Uso y Política de Privacidad se muestran en un modal interno al primer ingreso a la app.',
          'Móvil cliente y patrulla — "Política de Privacidad" en pantalla de Perfil ahora abre visor interno en lugar del navegador externo.',
        ],
      },
    ],
  },
  {
    version: '1.8.27',
    date: '2026-05-06',
    sections: [
      {
        type: 'added',
        items: [
          'Web — configurador de radio de patrullaje por cuenta (en "Configuración de servicios"). Valor por defecto 15 km, rango 1–100 km. Ajustable desde el panel sin recompilar apps.',
          'Móvil patrulla — asignación automática de alertas cuando el patrullero está en estado BACKGROUND (app en segundo plano). Se le notifica con alerta sonora y vibración de alta prioridad para que no pierda la asignación.',
          'Móvil — versión de la app visible en la pantalla de Perfil (ASI Security v1.8.27 / ASI Patrol v1.8.15).',
          'Móvil — enlace a Política de Privacidad en la pantalla de Perfil de ambas apps.',
          'Web — páginas de Política de Privacidad publicadas para Google Play Store (ASI Security y ASI Patrol).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Móvil cliente — la grilla de cámaras ya no se queda en diseño de 1 columna al regresar del visor en pantalla completa. El giro de pantalla al salir del visor ya no desordena el tamaño de las celdas.',
        ],
      },
    ],
  },
  {
    version: '1.8.26',
    date: '2026-05-06',
    sections: [
      {
        type: 'added',
        items: [
          'Web — botón "Desactivar / Reactivar sitio" en la pantalla de detalle del sitio. Al desactivar, el sitio desaparece de la app móvil del cliente (útil para suspender clientes morosos) pero queda en la base de datos para conservar el histórico de eventos. Reactivable en cualquier momento.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Móvil cliente — la app ya no muestra sitios desactivados desde el panel admin.',
          'Móvil cliente — corrección al congelamiento del botón de hablar (PTT): un doble-toque podía dejar el grabador nativo bloqueado. Ahora se recupera automáticamente y el botón vuelve a funcionar.',
          'Móvil cliente — armado/desarmado del panel ahora se cancela limpiamente tras 20 s si el panel no responde, en vez de dejar la UI colgada.',
          'Móvil patrulla — misma protección anti-doble-toque al grabar audio en alertas.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Móvil cliente — botón de hablar (PTT) ahora es grande y centrado, con texto "Mantén para hablar". Nuevo grupo de control de volumen del altavoz (−/silencio/+) en la esquina inferior derecha del visor de cámara.',
        ],
      },
    ],
  },
  {
    version: '1.8.21',
    date: '2026-05-08',
    sections: [
      {
        type: 'added',
        items: [
          'Móvil — audio de cámaras: el reproductor ahora solicita la pista de audio al servidor (AAC). Las cámaras con micrófono emitirán sonido; pulsa el ícono de volumen para activarlo.',
          'Móvil — audio bidireccional: cámaras Uniarch con altavoz integrado muestran el botón PTT (mantener pulsado para hablar). Se detecta automáticamente con el nuevo campo "supports_two_way_audio" en la DB.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Móvil — el reproductor abre silenciado por defecto; pulsa el ícono de volumen para activar el audio (evita sonido inesperado al abrir la cámara).',
          'Móvil — el botón PTT ya no aparece en cámaras sin altavoz (antes aparecía en todas las cámaras GATEWAY).',
        ],
      },
    ],
  },
  {
    version: '1.8.20',
    date: '2026-05-07',
    sections: [
      {
        type: 'added',
        items: [
          'Móvil — tarjeta de panel de alarma con información de estado enriquecida: banner rojo de "¡ALARMA!" o naranja de "Falla" cuando el panel lo reporta.',
          'Móvil — badge de estado del panel muestra los 4 estados reales: Online, Offline, ¡ALARMA!, Falla (con colores diferenciados).',
          'Móvil — pie de tarjeta del panel: última señal de vida (heartbeat), último evento y modelo del dispositivo.',
        ],
      },
    ],
  },
  {
    version: '1.8.19',
    date: '2026-05-05',
    sections: [
      {
        type: 'added',
        items: [
          'Móvil — botones de armar/desarmar rediseñados: pill buttons con iconos claros (Total / Parcial / Desarmar), ya no se ven alargados.',
          'Móvil — nuevo botón “Ver todo” en la pantalla Dispositivos: abre modal de pantalla completa con todas las cámaras de todos los sitios en cuadrícula.',
          'Móvil — selector de cuadrícula: 1, 2×2 o 4×4 cámaras (predeterminado 2×2). Toca cualquier miniatura para abrir el reproductor.',
        ],
      },
    ],
  },
  {
    version: '1.8.18',
    date: '2026-05-06',
    sections: [
      {
        type: 'added',
        items: [
          'Dispositivos — botón "Importar todo" en el escáner de red: importa todos los dispositivos disponibles en un solo paso sin necesidad de entrar a cada uno.',
          'Dispositivos — al pulsar Enter en cualquier campo de búsqueda (IP, usuario, contraseña, puerto) se inicia el escaneo automáticamente.',
          'Sitios — edición de nombre de cámara directamente desde la tarjeta en la pestaña Cámaras (ícono de lápiz → campo de texto inline).',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Dispositivos — ya no se hace re-descubrimiento automático al importar un dispositivo; el estado del escaneo se actualiza localmente.',
          'Móvil — las miniaturas en vivo (LiveThumb) ya no recortan el texto OSD de fecha/hora de la cámara; se usa ajuste "contain" en vez de "cover".',
        ],
      },
    ],
  },
  {
    version: '1.8.17',
    date: '2026-05-05',
    sections: [
      {
        type: 'fixed',
        items: [
          'Streaming — las miniaturas en vivo del móvil (LiveThumb) ya no intentan cargar el stream HLS de sub-canal para cámaras que no tienen sub-stream registrado (ej. InVid/cámaras sin rtsp_url_sub). Ahora muestran el snapshot estático como poster en vez de errores 404 repetidos.',
          'Streaming — el snapshot poster de cámaras sin sub-stream ahora usa el frame del stream principal en vez de fallar silenciosamente.',
          'Web — cámaras Patio y Frente (NVR DS-7608NI en Residencia Principal) ya visibles en web y móvil. Los registros anteriores eran huérfanos; reimportarlos desde el escáner de red creó los streams correctos en go2rtc.',
          'Gateway — el sync de go2rtc al arrancar ya no falla cuando la columna rtsp_url_sub no existe en la DB (la migración la agrega correctamente).',
        ],
      },
      {
        type: 'added',
        items: [
          'DB — nueva columna rtsp_url_sub en tabla cameras. Almacena la URL RTSP del sub-canal para cámaras no-Hikvision (Uniarch, InVid). El gateway la usa al registrar streams en go2rtc en vez de intentar derivarla por regex.',
          'DB — migración de datos: rtsp_url_sub poblado automáticamente para todas las cámaras Uniarch con URL /media/video1 → /media/video2.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.12',
    date: '2026-05-04',
    sections: [
      {
        type: 'added',
        items: [
          'Aprovisionamiento — el generador de scripts MikroTik (.rsc) ahora puede incluir configuración WiFi opcional. Al activarlo, el router genera una red inalámbrica oculta "ASI-Gateway" (WPA2) que conecta las cámaras inalámbricas al mismo bridge de red que las cableadas, uniéndolas a la VPN automáticamente.',
          'Aprovisionamiento — el .rsc ahora también establece la contraseña del administrador del router como parte del script de configuración inicial.',
          'Web — nuevo toggle "WiFi en el .rsc" en la pestaña Gateway de cada sitio y en la tarjeta de gateway de cuenta, con información de SSID, seguridad y bridge cuando está activo.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.11',
    date: '2026-05-04',
    sections: [
      {
        type: 'fixed',
        items: [
          'Web — modal de creación/edición de usuario: botones "Cancelar" y "Guardar" no eran visibles cuando el formulario era largo. Ahora el modal tiene estructura fija (header + contenido scrollable + footer) con max-height 90vh.',
          'Web — sidebar ahora solo se colapsa automáticamente si fue expandido automáticamente (click en item padre estando colapsado). Si el usuario lo expande manualmente, permanece expandido al navegar a hijos.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Web — página de alertas reorganizada con 3 pestañas: Pendientes (PENDING), En Proceso (ASSIGNED/EN_ROUTE/ON_SCENE/IN_PROGRESS), Historial (RESOLVED/CLOSED/ESCALATED/CANCELLED). Ordenamiento por fecha más reciente primero.',
          'Web — presentación mejorada de alertas en lista: indicador de tipo más grande con color, ticket number destacado, fecha completa con formato legible, ubicación con ícono MapPin, indicador de patrullero asignado.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.10',
    date: '2026-05-04',
    sections: [
      {
        type: 'added',
        items: [
          'Web — redirect canónico en producción: si el dashboard se carga desde la URL directa de CloudFront (d3nq08kxwmx6b1.cloudfront.net), React redirige automáticamente a asisecurity.corporacionasi.com preservando path, query y hash.',
          'Patrol + Cliente — los mapas de alerta ahora consultan la edge function get-route (proxy de Google Directions API) y dibujan rutas reales por calles en vez de línea recta. Se recalcula automáticamente si el patrullero se mueve >50m.',
          'Web — al hacer click en un marker de patrullero en el mapa de emergencias, el InfoWindow ahora permanece abierto (sticky) hasta que el usuario haga click en el mapa o en otro marker. Muestra nombre, estado, teléfono y última actualización.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Web — despliegue a AWS CloudFront + S3: el dashboard ahora se sirve desde S3 bucket asisecurity-dashboard con distribución CloudFront, SSL vía ACM certificate, y DNS corporativo asisecurity.corporacionasi.com. Script de deploy automatizado (scripts/deploy-web.ps1) construye con Vite, sincroniza a S3 con headers de cache optimizados, e invalida CloudFront en ~30s.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Cliente — permiso CAMERA faltante: en Android no se podía tomar foto desde Perfil ni adjuntar imágenes al chat porque AndroidManifest.xml no declaraba <uses-permission CAMERA />.',
          'Patrol — "Alerta no encontrada" al abrir desde notificación push: race condition cuando el push llega antes de la sincronización Realtime. Ahora se reintenta hasta 3 veces con delay de 1.5s entre intentos (total 4.5s).',
          'Patrol — audio de alerta en loop infinito: el reproductor disparaba setIsFinished(true) dos veces (ProgressEvent + Subscription) causando reproducción continua. Ahora se garantiza un solo set con flag compartido.',
          'Patrol — app cierra sola al abrir alerta desde push: si la notificación abría AlertDetailScreen con el stack vacío, navigation.goBack() crasheaba. Ahora se valida canGoBack() y se navega a Alerts como fallback.',
          'Patrol — crash al renderizar ChatBubble con imagen 404: si message.media_url retornaba error HTTP, <Image> rompía el render del chat. Handler onError ahora captura el fallo y muestra fallback "⚠️ Error al cargar imagen".',
          'Web — error de compilación TypeScript en PatrolList.tsx: getPatrolStatusName() incluía OFF_DUTY en el Record<Patrol[\'status\']> pero el tipo Patrol no define ese estado.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.9',
    date: '2026-05-03',
    sections: [
      {
        type: 'changed',
        items: [
          'Mobile-client — ícono de la app actualizado al logo oficial ASI (triángulo equilátero amarillo/azul/negro sobre fondo blanco). El ícono anterior tenía el triángulo recortado.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.8',
    date: '2026-05-03',
    sections: [
      {
        type: 'fixed',
        items: [
          'Emergencias — al minimizar el patrullero, el listado mostraba la patrulla sin texto de estado (parecía "perderse"). El componente `PatrolList` no tenía mapeo para el estado `BACKGROUND`; ahora se renderiza correctamente como "Segundo plano" en cyan y la patrulla sigue visible bajo "Disponibles".',
          'Mapa de Emergencias — al cambiar el ícono del marcador de un cliente o patrullero desde Usuarios, el mapa no actualizaba el ícono hasta recargar. Se agregó suscripción Realtime al canal `arc-profiles` que refresca patrullas y clientes en cuanto un perfil cambia.',
          'Mobile-client — recompilado a v1.8.14.7 (vc=20) para incluir el retry de auth-store + refresh JWT en location-service que resuelve el "queda colgado cargando" tras minimizar/regresar.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.7',
    date: '2026-05-03',
    sections: [
      {
        type: 'added',
        items: [
          'Patrullero — la app ahora reporta `BACKGROUND` cuando el patrullero la minimiza y `AVAILABLE` cuando vuelve al frente, igual que el cliente. La operadora ve el cambio inmediatamente en el mapa de Emergencias (cyan "Segundo plano" / verde "Disponible") sin esperar al próximo heartbeat. Requiere migración `20260503030000_patrol_status_background.sql` que añade el valor `BACKGROUND` al enum `patrol_status`.',
          'Usuarios — el ícono del marcador en el mapa ahora se puede elegir también para patrulleros (no solo clientes). Se añadieron dos íconos nuevos al catálogo compartido: 🏍️ Motocicleta (default para patrullas) y 🛡️ Patrullero.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.6',
    date: '2026-05-03',
    sections: [
      {
        type: 'fixed',
        items: [
          'Patrullero — la app no podía actualizar `patrol_locations` y la operadora veía siempre OFFLINE en el mapa de Emergencias, aunque la sesión, el JWT y el patrol_id fueran correctos. Causa raíz: las RLS policies originales de `patrol_locations` (creadas con role `{public}` y modificadas por la migración de "fix_rls_infinite_recursion") quedaron en un estado inconsistente y el planner nunca las aplicaba al rol `authenticated`, por lo que cualquier INSERT/UPDATE devolvía 42501 "new row violates row-level security policy". Se recrearon como `TO authenticated` y se añadió una SELECT propia para que el patrullero pueda leer su propia ubicación.',
          'Patrullero — el heartbeat de presencia (cada 30 s en foreground) ya no usa upsert: la columna `location` es NOT NULL y el INSERT-path del upsert sin GPS fallaba con 23502. Ahora hace UPDATE plano sobre el row sembrado por el trigger.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.4',
    date: '2026-05-03',
    sections: [
      {
        type: 'fixed',
        items: [
          'Emergencias — los marcadores de clientes a veces se quedaban en violeta ("En línea") aunque el dispositivo ya hubiera reportado BACKGROUND. El polling de seguridad se redujo de 60 s a 15 s y el mapa ahora re-evalúa el estado cada 5 s para reflejar cambios aunque se pierda algún evento Realtime.',
          'Emergencias — diagnóstico añadido (console.log) para los canales Realtime de tickets, patrol_locations y client_locations: ahora se ve en la consola del navegador cuándo un canal queda SUBSCRIBED y cuándo recibe eventos.',
          'Patrol app — patrullero estacionado/quieto desaparecía del mapa de la operadora a los ~3 minutos. Causa: BackgroundGeolocation solo emite onLocation al moverse >50 m, así que el row de patrol_locations se ponía rancio. Solución: activado heartbeatInterval=60s + listener onHeartbeat que refresca status=AVAILABLE y updated_at incluso parado y en background.',
          'Cliente app — si después de un reload de JavaScript Supabase Auth se quedaba con el lock tomado (deadlock), getSession() expiraba a los 8 s y la app quedaba sin sesión: el location-service seguía mandando upserts pero ya no llegaban a la BD y el dashboard mostraba al cliente como desconectado. Ahora initialize() reintenta hasta 3 veces con back-off de 1 s, y location-service detecta errores JWT y fuerza refreshSession() automáticamente.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.3',
    date: '2026-05-03',
    sections: [
      {
        type: 'fixed',
        items: [
          'Cliente — la tarjeta "Alarmas de paneles (24h)" del Home siempre se mostraba vacía porque la consulta a arc_events pedía columnas inexistentes (cid_event_code, description) y fallaba con "column arc_events.cid_event_code does not exist". Se corrigió a los nombres reales (cid_code, zone_name).',
        ],
      },
    ],
  },
  {
    version: '1.8.14.2',
    date: '2026-05-03',
    sections: [
      {
        type: 'changed',
        items: [
          'Patrol — al finalizar una alerta ahora se muestra un diálogo "Alerta finalizada" con el mensaje de que la boleta se registró y el cliente fue notificado, en lugar de salir silenciosamente al listado.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.1',
    date: '2026-05-03',
    sections: [
      {
        type: 'changed',
        items: [
          'Patrol — la firma de la boleta de visita ya no se captura tomando una foto a un papel: ahora se dibuja directamente en pantalla con el dedo o un stylus sobre un lienzo SVG, se rasteriza a PNG y se sube al bucket evidence. El modal incluye un botón "Limpiar firma" para reiniciar el trazo antes de finalizar.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Patrol — autoscroll del chat de la alerta: el FlatList ahora se ancla al último mensaje en onContentSizeChange y onLayout (además del scroll inicial), por lo que los mensajes nuevos quedan visibles sin tener que scrollear manualmente.',
        ],
      },
    ],
  },
  {
    version: '1.8.14.0',
    date: '2026-05-03',
    sections: [
      {
        type: 'added',
        items: [
          'DB — número de boleta trazable: ahora siguen el formato ASI-<TIPO>-YYYYMMDD-NNNN (por ejemplo ASI-SOS-20260503-0042 o ASI-MEDICA-20260503-0043). El tipo se humaniza al español (PANIC→PANICO, FIRE→INCENDIO, MEDICAL→MEDICA, CALL→LLAMADA…) para identificar la boleta sin abrirla.',
          'Patrol — boleta de visita al finalizar una alerta: nuevo modal que captura quién atendió, observaciones y firma (foto). El resumen y la imagen de la firma se publican automáticamente en el chat del ticket; la operadora y el cliente las ven en tiempo real, y la información queda persistida en tickets (closure_type=ATENDIDO, closure_notes, closed_by).',
          'Patrol — selector de tema Claro / Oscuro en Perfil con persistencia. Aplica a Login, Perfil, Alertas, Historial y la barra de navegación; Mapa y detalle de alerta se mantienen en oscuro por su densidad de información.',
          'Patrol — botón mostrar/ocultar contraseña (icono de ojo) en la pantalla de inicio de sesión.',
        ],
      },
      {
        type: 'changed',
        items: [
          'Web + móviles — terminología "RESOLVED" → "Finalizada". El dashboard, las apps cliente y patrullero ahora muestran "Finalizada"/"Finalizar Alerta" en lugar de "Resuelta"/"Resolver"/"Resolved".',
          'Móviles — los mensajes de sistema en el chat (cambios de estado) ahora muestran HH:mm al final del texto para reconstruir la línea de tiempo de la alerta.',
          'Patrol — el chat ahora muestra el nombre real del remitente al cargar mensajes históricos (eliminado el "usuario · usuario" que aparecía al reabrir una alerta).',
        ],
      },
    ],
  },
  {
    version: '1.8.13.5',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Web — el cliente siempre aparecía “En línea” y los patrulleros nunca aparecían “Disponible” porque el dashboard solo confiaba en el campo status de client_locations / patrol_locations. Ese campo solo se refresca con un upsert vivo del dispositivo, así que un crash, un kill del SO o una caída de internet dejaba la fila en ONLINE/AVAILABLE para siempre. Ahora el store degrada a OFFLINE cuando updated_at supera 180s (3-6 latidos perdidos).',
          'Web — la migración 20260502000000_disk_io_optimizations había sacado client_locations y patrol_locations de la publicación supabase_realtime, así que el dashboard ARC reaccionaba con minutos de retraso. Nueva migración 20260502080000_relive_locations_realtime las re-publica (a nuestra escala el WAL extra es despreciable y la ventana de stale cubre el resto).',
        ],
      },
    ],
  },
  {
    version: '1.8.13.4',
    date: '2026-05-02',
    sections: [
      {
        type: 'added',
        items: [
          'Cliente — filtros Pendientes / Procesadas en “Mis Alertas” (Historial). Mismo patrón que la operadora web.',
          'Cliente — realtime de tickets en HomeScreen y AlarmsScreen: el cambio de estado (PENDING→ASSIGNED→EN_ROUTE…) se refleja al instante en lugar de esperar al pull-to-refresh.',
          'Web — pestaña Patrulleros muestra contador (activos/total) en el botón.',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Patrol — patrulleros aparecían OFFLINE en foreground porque startTracking solo corría al montar MapScreen. Nuevo PatrolPresenceController centralizado: arranca tracking al login y envía heartbeat cada 30s en foreground (sin GPS) para mantener AVAILABLE en patrol_locations.',
        ],
      },
    ],
  },
  {
    version: '1.8.13.3',
    date: '2026-05-02',
    sections: [
      {
        type: 'changed',
        items: [
          'Patrol — logo corporativo en splash de carga, login y perfil.',
          'Cliente — “Alertas activas” en Inicio se ordenan por estado: en progreso (IN_PROGRESS / EN_ROUTE / ON_SCENE / ASSIGNED) arriba con la más reciente primero, PENDING al fondo.',
          'Patrol — “Alertas” asignadas: IN_PROGRESS arriba (más recientes primero), ASSIGNED recién recibidas (aún no aceptadas) al fondo.',
          'Web — cola de alertas en Emergency con pestañas Pendientes / Procesadas. Pendientes (PENDING) por defecto; Procesadas agrupa todo lo activo que ya tiene operador trabajando.',
        ],
      },
    ],
  },
  {
    version: '1.8.13.2',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Patrol — crash al enviar audio en chat de evidencias (y al reabrir la alerta): el ChatBubble referenciaba <InlineAudio> pero el componente nunca fue declarado al portar la grabación desde cliente. Añadido el componente faltante.',
        ],
      },
    ],
  },
  {
    version: '1.8.13.1',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Las 3 apps quedaban en spinner infinito tras 1.8.13: la nueva policy de profiles consultaba tickets y ticket_messages directamente, pero ambas tienen RLS que vuelve a consultar profiles → recursión infinita. Reemplazada por función SECURITY DEFINER is_ticket_counterpart().',
        ],
      },
    ],
  },
  {
    version: '1.8.13',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Apps móviles: los chats mostraban "Usuario · Usuario" porque la RLS de profiles no permitía a cliente y patrullero leer el perfil del otro. Nueva política que expone solo nombre y rol entre contrapartes del mismo ticket o autores de mensajes en él',
          'Patrol: el chat no mostraba nombres en la primera carga porque el SELECT inicial no joineaba profiles. Ahora carga full_name+role igual que el cliente y sube el límite a 500 mensajes',
          'Patrol: deadlock al avanzar a EN SITIO / RESUELTO — el handler realtime de tickets UPDATE refetcheaba el ticket completo en paralelo con el UPDATE+INSERT+invoke en curso, las queries colis ionaban en el auth-lock y la pantalla se congelaba. Ahora se actualiza en memoria con el payload',
          'Patrol: la tarjeta de evidencia estaba debajo del chat. Movida arriba para mantener paridad con el cliente',
          'Patrol: fotos/videos/audios en el chat se veían como pills con icono. Ahora cada burbuja muestra miniatura inline (imagen 220×220, video con controles, audio con play/pause inline)',
        ],
      },
      {
        type: 'added',
        items: [
          'Cliente: los cambios de estado del patrullero (EN RUTA / EN SITIO / RESUELTO) ya se renderizan como píldoras de sistema centradas dentro de la línea cronológica del chat, junto a los mensajes y multimedia',
        ],
      },
    ],
  },
  {
    version: '1.8.12',
    date: '2026-05-02',
    sections: [
      {
        type: 'added',
        items: [
          'Patrol móvil: grabación de audio en el chat con el mismo flujo del cliente — cuarto botón "Audio" en la tarjeta Enviar evidencia, banner ● REC con timer y cancelar, sube a evidence/<ticketId>/...m4a',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Cliente móvil: el mapa de seguimiento de alerta dibujaba al patrullero en (0,0) cuando el patrullero aún no tenía primer fix de GPS (el seed de patrol_locations es null-island). Mismo guardia que ya tenía la web: ahora se ignoran fixes con |lat|<0.01 y |lng|<0.01 tanto en la carga inicial como en el INSERT realtime',
        ],
      },
    ],
  },
  {
    version: '1.8.11',
    date: '2026-05-02',
    sections: [
      {
        type: 'added',
        items: [
          'Cliente + Patrol móvil: cada burbuja del chat ajena ahora muestra "Nombre · Rol" (Cliente / Patrullero / Operador / Supervisor / Admin), igual que en la web',
          'Patrol móvil: composer de chat con paridad total al cliente — tarjeta "Enviar evidencia" con botones explícitos Foto / Video / Galería (reemplaza el menú detrás del 📎)',
          'Patrol móvil: cada cambio de estado (EN_RUTA / EN_SITIO / RESUELTA / CERRADA) ahora (a) inserta un mensaje system en el chat para auditoría visible y (b) envía push FCM al cliente con deep-link a la pantalla de seguimiento',
          'Edge function notify-ticket-status: clon del template de notify-patrol-assignment, envía FCM v1 a todos los miembros del cliente (account_members → profiles.fcm_token)',
        ],
      },
      {
        type: 'changed',
        items: [
          'Historial de alertas: aumentado el límite de la consulta de 50 → 500 (cliente móvil) y 100 → 500 (web). El usuario reportaba que el listado se truncaba en ~50 boletas',
          'Web TicketChatPanel: la etiqueta de remitente ahora muestra "Nombre · Rol" (antes solo nombre, o "Patrullero"/"Cliente" genérico)',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Patrol APK 1.8.11 incluye el fix de auth-lock (processLock) que se agregó en 1.8.10b — la pantalla "Cargando…" infinita ya no ocurre después del login en el patrullero',
        ],
      },
    ],
  },
  {
    version: '1.8.10',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Móvil (cliente y patrol): pantalla "Cargando…" eterna después del login. Causa raíz: Supabase JS v2 intenta usar Navigator Locks sobre localStorage para serializar el refresh-token, API que no existe en React Native; la promesa quedaba colgada y useAuthStore nunca hidrataba la sesión. Fix: pasar auth.lock = processLock al crear el cliente. Desbloquea el cold-start y la rehidratación después de 5+ min sin abrir la app',
        ],
      },
    ],
  },
  {
    version: '1.6.1',
    date: '2026-05-02',
    sections: [
      {
        type: 'fixed',
        items: [
          'Reproductor de cámara: "illegal codec" en streams HEVC del NVR — ahora se fuerza transcodificación H.264 vía go2rtc (&video=h264&mp4) en HLS',
          'ExoPlayer crash IllegalArgumentException por bufferConfig inválido (CameraPlayerScreen y CamerasScreen)',
          'LiveThumb: cuando había 2+ cámaras en un sitio sólo reproducía la última (cold-start race en go2rtc). Solución preservando on-demand: el cliente llama POST /api/go2rtc/warm al abrir el sitio (gateway adjunta preload temporal por stream) y POST /api/go2rtc/cool al cerrarlo, dejando los pipelines RTSP apagados cuando nadie mira',
          'Supabase advisor "Security Definer View" sobre site_cameras: vista recreada con security_invoker = true',
        ],
      },
      {
        type: 'changed',
        items: [
          'apps/mobile-client services/cameras.ts: removido buildGatewayMp4Url, todos los URLs pasan por HLS H.264 forzado',
          'gateway: nuevo endpoint POST /api/go2rtc/preload-all para back-fill de preload en streams ya registrados',
          'Nueva migración 20260502010000_security_advisors_fixes.sql',
        ],
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-05-01',
    sections: [
      {
        type: 'added',
        items: [
          'Móvil: chat de tickets con multimedia real (imagen, video con controles, audio play/pause)',
          'Móvil: grabación de audio en el chat (hold-to-stop) con permisos RECORD_AUDIO',
          'Móvil Home: tarjetas de Alertas activas (gestionables → AlertTracking) y Alarmas 24h con refresh 60s',
          'Web operador: TicketChatPanel con realtime y firmado de URLs',
          'Web emergencia: mini-mapa Uber-style ahora muestra #ticket_number y nombre de cliente',
          'Configuración del SOS por agitación (sensibilidad y cuenta regresiva)',
          'SOS por agitación disponible también en mobile-patrol',
          'AccountDetailPage: pestaña de destinatarios de alerta por cuenta',
          'Vista de tickets de miembros visible para account owner',
          'docs/RETENTION_POLICY.md — política de retención por plan (diseño)',
        ],
      },
      {
        type: 'changed',
        items: [
          'Bucket evidence pasa a privado: rutas se firman bajo demanda con createSignedUrl(60m)',
          'Botón móvil "Historial" renombrado a "Alarmas"',
          'AlertDetail/TicketChatPanel con tema dual light/dark completo',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Web chat: composer con texto invisible (text-white sobre fondo claro)',
          'Web chat: auto-scroll al último mensaje aunque la multimedia tarde en cargar',
          '404 al abrir evidencia (getPublicUrl en bucket privado)',
          'ArcEventsPage: type error en batchSiteConfig (siteId opcional)',
        ],
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-04-30',
    sections: [
      {
        type: 'added',
        items: [
          'Tracking de ubicación de clientes (background) en mobile-client',
          'Iconos de marcador personalizables (initials, foto de perfil) compartidos',
          'Página de Auditoría con filtros y export',
          'Sounds personalizados para alertas en web',
        ],
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-04-28',
    sections: [
      {
        type: 'added',
        items: [
          'Mapa de emergencia rediseñado tipo Uber (mini-mapa para alerta activa)',
          'ClientList y PatrolList laterales en EmergencyPage',
          'Edge function process-alert para ruteo y push',
        ],
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-04-25',
    sections: [
      {
        type: 'added',
        items: [
          'Sistema RBAC completo con permisos granulares',
          'Página de Roles con asignación por usuario',
          'Página de Usuarios con creación vía Edge Function create-user',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-04-16',
    sections: [
      {
        type: 'added',
        items: [
          'Módulo de Emergencia con cola de alertas y mapa global',
          'Notificaciones push FCM end-to-end',
        ],
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-04-16',
    sections: [
      {
        type: 'added',
        items: [
          'Sistema de provisioning de sitios (site-onboard.ps1)',
          'WireGuard VPN para gateways en sitios remotos',
          'Topología de red documentada',
        ],
      },
    ],
  },
  {
    version: '0.9.0',
    date: '2026-04-01',
    sections: [
      {
        type: 'added',
        items: [
          'go2rtc como engine alternativo de captura',
          'Cámaras HCT (Hik-Connect) streaming via gateway',
        ],
      },
    ],
  },
  {
    version: '0.8.0',
    date: '2026-03-29',
    sections: [
      {
        type: 'added',
        items: [
          'Soporte completo Hikvision HPP para paneles de alarma',
          'Sincronización masiva de paneles desde Partner Pro',
        ],
      },
    ],
  },
  {
    version: '0.7.0',
    date: '2026-03-29',
    sections: [
      {
        type: 'added',
        items: [
          'Página ARC Events con dashboard de eventos en tiempo real',
          'Procesamiento por lote de eventos (batch processing)',
          'Códigos CID Ademco precargados (~200)',
        ],
      },
    ],
  },
  {
    version: '0.6.0',
    date: '2026-03-28',
    sections: [
      {
        type: 'added',
        items: [
          'Módulo ARC completo para Central Receptora de Alarmas',
          'Centro ARC (/arc/events) con dashboard de eventos en tiempo real',
          'Bitácora de eventos (/arc/historial) con filtros, exportación y paginación',
          'Contactos ARC (/arc/contacts) con CRUD completo por sitio',
          'Códigos CID (/arc/cid-codes) con ~200 códigos Ademco estándar',
          'Polling MQ y WebSocket para recepción de eventos HPP en tiempo real',
          'ArcRealtimeProvider con sonidos de notificación para eventos críticos',
          'Vinculación de cámaras a sitios de alarma',
        ],
      },
      { type: 'fixed', items: ['Logging mejorado en sincronización de paneles HPP'] },
    ],
  },
  {
    version: '0.5.0',
    date: '2026-03-28',
    sections: [
      {
        type: 'added',
        items: [
          'Esquema ARC extendido (migración 20260328000003)',
          'Tabla arc_contacts, arc_events, arc_call_log, cid_codes',
          'Tipos TypeScript en @asi/shared: ArcContact, ArcEvent, ArcCallLog, CidCode',
        ],
      },
      {
        type: 'fixed',
        items: [
          'Credenciales HCT no cargaban en Gateway (ESM hoisting)',
          'Tabla alarm_panels no existía (migración no ejecutada)',
          'RLS bloqueaba INSERT en tablas de alarmas',
        ],
      },
    ],
  },
  {
    version: '0.4.0',
    date: '2026-03-27',
    sections: [
      {
        type: 'added',
        items: [
          'Integración Hik-Connect (HCT) con OAuth2',
          'Integración Hik Partner Pro (HPP) para paneles de alarma',
          'Página de Alarmas (/alarms) con lista de paneles y eventos',
        ],
      },
    ],
  },
  {
    version: '0.3.0',
    date: '2026-03-26',
    sections: [
      {
        type: 'added',
        items: [
          'Gateway ONVIF para streaming de cámaras Hikvision',
          'Conversión RTSP → HLS via FFmpeg',
          'Grid de vista múltiple de cámaras',
        ],
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-03-25',
    sections: [
      {
        type: 'added',
        items: [
          'Dashboard con métricas en tiempo real',
          'Gestión de tickets/alertas con workflow de estados',
          'Mapa de patrulleros con tracking GPS',
          'Geocercas con editor visual',
        ],
      },
      {
        type: 'security',
        items: ['RLS en todas las tablas', 'Autenticación Supabase Auth', 'Roles y permisos'],
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-03-20',
    sections: [
      {
        type: 'added',
        items: [
          'Estructura inicial del monorepo (pnpm workspaces)',
          'Aplicación web React + Vite + TailwindCSS',
          'Apps móviles cliente y patrullero (React Native CLI)',
          'Configuración de Supabase (PostgreSQL + PostGIS)',
          'Docker Compose para Jitsi Meet + Gateway + Nginx',
        ],
      },
    ],
  },
];

export const CURRENT_VERSION =
  CHANGELOG_DATA.find((e) => e.version !== 'Unreleased')?.version ?? '0.0.0';
